import React, { useState, useEffect, useCallback, useId } from 'react';
import { useTranslation } from 'react-i18next';
import { organizationAPI } from '../services/api';
import { Plus, Pencil, Trash2, Loader2, ChevronDown, ChevronRight, AlertCircle, Store, Link2, Unlink, Info, X, Check, EyeOff, Lock } from 'lucide-react';
import { SheetField } from './settings/sheet';
import {
  DetailPanel, Disclosure, EmptyState, GHOST_BTN, INPUT_CLS, ListPage, ListToolbar, PRIMARY_BTN, SELECT_CLS,
} from './shared/listPage';
import {
  Banner, COMPACT_INPUT, FlashToast, ROW_DANGER_BTN, ROW_ICON_BTN, SubTabs, chipClass,
} from './orgPages/ui';
import {
  CatalogMode, OverrideMode, PlatformName, PriceListPlatformDuplicateEntry, PriceListPlatformRow, PriceOverride,
  PLATFORM_PRICE_EPSILON, catalogErrorText,
  isCatalogForbidden, isCatalogRouteMissing, isPlatformRowNoteworthy,
  orgPlatformCatalogAPI, priceListPlatformKey, rowDuplicateCount, rowDuplicateEntries, rowMasterKeys,
  rowPricesItemLevel,
} from '../services/platformCatalogApi';
import { orgPlatformMappingsAPI } from '../services/platformMappingApi';
import { isFeeLockProduct, priceListRowLock, PriceListRowLock } from '../utils/priceListRowLock';

interface Props {
  orgId: number;
  canManage: boolean;
}

interface PriceList {
  id: number;
  name: string;
  store_count: number;
  display_order: number;
}

// Στήλες τιμής ΜΕΓΕΘΟΥΣ: base = η απόλυτη τιμή όταν η στήλη καναλιού είναι NULL· οι τρεις
// στήλες καναλιού την παρακάμπτουν ανά κανάλι. Γραμμές τιμής προϊόντος δεν υπάρχουν (Φ5): η
// τιμή ζει μόνο στο μέγεθος, το τέλος στο state_fees.
const SIZE = { base: 'price_modifier', ta: 'price_modifier_take_away', di: 'price_modifier_dine_in', dl: 'price_modifier_delivery' } as const;
const CHAN: ('ta' | 'di' | 'dl')[] = ['ta', 'di', 'dl'];
const CHAN_LABEL: Record<string, string> = { ta: 'Take-away', di: 'Dine-in', dl: 'Delivery' };

type Cell = { base: string; ta: string; di: string; dl: string };

/* Νέα εμφάνιση (14/10/2026): ο πίνακας τιμών στο ύφος του «Πίνακα» (shared/listPage) — επικεφαλίδες
   t3, λεπτές γραμμές, χωρίς κουτί· τα πεδία τιμής με tokens (όχι μπλε)· τα μικρά πάνελ efood στην
   επιφάνεια της εφαρμογής. Λογική, κλήσεις και κείμενα όπως πριν. */
const TH = 'px-3 py-2 text-[12.5px] font-semibold text-t3';
const PRICE_INPUT = 'h-9 px-2.5 bg-surface-2 border border-line-strong rounded-lg text-sm text-right tabular-nums '
  + 'focus:outline-none focus:ring-2 focus:ring-ring/50 focus:border-ring disabled:opacity-60';
const POPOVER = 'fixed z-50 rounded-xl border border-line bg-surface-1 p-3 shadow-[0_16px_40px_-12px_rgb(0_0_0_/_0.3),0_2px_6px_rgb(0_0_0_/_0.08)]';

/** Οι γραμμές μεγέθους ενός τιμοκαταλόγου (`GET …/prices` → `{ sizes }`) ανά org_size_id. */
const sizeRowsById = (d: any): Record<number, any> => {
  const s: Record<number, any> = {};
  ((d && d.sizes) || []).forEach((r: any) => { s[r.org_size_id] = r; });
  return s;
};

// ───────────────── Στήλη efood (Φ5β) ─────────────────
//
// ΓΙΑΤΙ ΕΔΩ: η Κασσιανή ανοίγει ΑΥΤΗ την οθόνη κάθε φορά που αλλάζει τιμή. Ο
// επεξεργαστής καταλόγου (καρτέλα «Πλατφόρμες») μένει για τον ιδιοκτήτη —
// μαζικές ενέργειες, μετάπτωση, αποστολή. Εδώ μπαίνει ΜΙΑ στήλη δίπλα στο
// Delivery: «τι βλέπει ο πελάτης στο efood για αυτό το προϊόν».
const EFOOD: PlatformName = 'efood';
/** Πλάτος/ύψος του πάνελ — χρειάζονται για να μη βγει εκτός οθόνης. */
const PANEL_W = 320;
const PANEL_H = 340;

/**
 * Οι τρεις αποφάσεις, ΕΝΑ σημείο ορισμού: τις γράφει και η γραμμή (μία απόφαση)
 * και κάθε καταχώρηση διπλοεγγραφής (μία ανά είδος efood). Το κοντό κείμενο
 * είναι για τη λίστα των καταχωρήσεων, όπου χωράνε τρία κουμπιά σε μία σειρά.
 */
const DECISION_OPTS: Array<[OverrideMode, string, string]> = [
  ['catalog', 'βάλε την τιμή μας', 'η τιμή μας'],
  ['keep_platform', 'κράτα την τιμή efood', 'η τιμή efood'],
  ['fixed', 'σταθερή τιμή', 'σταθερή'],
];
const DECISION_LABEL: Record<OverrideMode, string> = {
  catalog: 'ακολουθεί τη δική μας τιμή',
  keep_platform: 'κρατά την τιμή efood',
  fixed: 'σταθερή τιμή',
};

/** Πού ανοίγει το πάνελ. `fixed` θέση: το πλέγμα έχει overflow-x και θα το έκοβε. */
type Anchor = { top: number; left: number };
type EfoodPanel = { key: string; label: string; row: PriceListPlatformRow; at: Anchor };
type EfoodAsk = {
  key: string; label: string; row: PriceListPlatformRow;
  productId: number; sizeId: number | null; at: Anchor;
};

/** Θέση πάνελ κοντά στο κελί, πάντα μέσα στο παράθυρο. */
const anchorOf = (el: HTMLElement): Anchor => {
  const r = el.getBoundingClientRect();
  const maxLeft = Math.max(8, window.innerWidth - PANEL_W - 12);
  const maxTop = Math.max(8, window.innerHeight - PANEL_H - 12);
  return { top: Math.min(Math.max(8, r.bottom + 6), maxTop), left: Math.min(Math.max(8, r.left - 180), maxLeft) };
};

// Pricing matrix (size × channel) with the price-list as a lens. The catalog
// STRUCTURE lives in the Προϊόντα tab; here you only set prices. "Βασικός" writes
// the catalog defaults (applied immediately); a named list writes overrides and
// sends approval requests to the stores on that list.
const OrgPriceListsManager: React.FC<Props> = ({ orgId, canManage }) => {
  const { t } = useTranslation(['admin', 'common', 'orgPages']);
  // Το id της φόρμας «Νέος τιμοκατάλογος / Μετονομασία» (κουμπί υποβολής στο footer του συρταριού).
  const modalFormId = useId();
  const [lists, setLists] = useState<PriceList[]>([]);
  const [products, setProducts] = useState<any[]>([]);
  const [selected, setSelected] = useState<number | 'default'>('default');
  /** Γραμμές μεγέθους του επιλεγμένου τιμοκαταλόγου, ανά org_size_id. */
  const [overrides, setOverrides] = useState<Record<number, any>>({});
  const [draft, setDraft] = useState<Record<string, Cell>>({});
  const [unlinked, setUnlinked] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [search, setSearch] = useState('');
  const [bulkPct, setBulkPct] = useState('10');
  const [bulkBusy, setBulkBusy] = useState(false);
  const [stores, setStores] = useState<any[]>([]);
  const [showStores, setShowStores] = useState(false);
  const [modal, setModal] = useState<{ mode: 'create' | 'rename'; id?: number; name: string; copyFromId?: number | null } | null>(null);
  const [modalBusy, setModalBusy] = useState(false);
  const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  // ── Στήλη efood (Φ5β) ──────────────────────────────────────────────────
  /** Γραμμές efood της ΟΜΑΔΑΣ, με το ίδιο κλειδί γραμμής (`ek`) με το πλέγμα. */
  const [efood, setEfood] = useState<Record<string, PriceListPlatformRow>>({});
  /** Η στήλη υπάρχει ΜΟΝΟ όταν η ομάδα έχει εισαγμένο κατάλογο efood. */
  const [efoodOn, setEfoodOn] = useState(false);
  const [efoodMode, setEfoodMode] = useState<CatalogMode>('read');
  /** ΤΙ ΕΠΙΤΡΕΠΕΤΑΙ, όπως το λέει ο ίδιος ο server (can_decide / can_map). */
  const [efoodCanDecide, setEfoodCanDecide] = useState(false);
  const [efoodCanMap, setEfoodCanMap] = useState(false);
  const [efoodLoading, setEfoodLoading] = useState(false);
  /** Γνήσια βλάβη (όχι 403/404): το λέμε, αλλιώς η απουσία στήλης είναι ψέμα. */
  const [efoodErr, setEfoodErr] = useState<string | null>(null);
  const [efoodStoreNames, setEfoodStoreNames] = useState<Record<number, string>>({});
  const [onlyEfoodDiff, setOnlyEfoodDiff] = useState(false);
  const [panel, setPanel] = useState<EfoodPanel | null>(null);
  const [panelMode, setPanelMode] = useState<OverrideMode>('catalog');
  const [panelFixed, setPanelFixed] = useState('');
  const [panelBusy, setPanelBusy] = useState(false);
  const [panelErr, setPanelErr] = useState<string | null>(null);
  /**
   * ΔΙΠΛΟΕΓΓΡΑΦΗ: η απόφαση είναι ΑΝΑ ΚΑΤΑΧΩΡΗΣΗ efood, όχι ανά γραμμή — άρα
   * ούτε το `panelMode` ούτε το `panelFixed` αρκούν. Κρατάμε ΠΟΙΑ καταχώρηση
   * ζητά ποσό (`dupFixedFor` = master_key) και ποια γράφεται τώρα.
   */
  const [dupFixedFor, setDupFixedFor] = useState<string | null>(null);
  const [dupFixed, setDupFixed] = useState('');
  const [dupBusyKey, setDupBusyKey] = useState<string | null>(null);
  const [ask, setAsk] = useState<EfoodAsk | null>(null);
  const [askBusy, setAskBusy] = useState(false);
  const [askErr, setAskErr] = useState<string | null>(null);

  const showToast = (type: 'success' | 'error', message: string) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 2500);
  };
  const fmt = (n: number) => new Intl.NumberFormat('el-GR', { style: 'currency', currency: 'EUR' }).format(Number(n) || 0);
  const ek = (id: number, isSize: boolean) => `${isSize ? 's' : 'p'}${id}`;

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [plData, prodData, storeData] = await Promise.all([
        organizationAPI.getPriceLists(orgId),
        organizationAPI.getCatalogProducts(orgId),
        organizationAPI.getOrgStores(orgId),
      ]);
      setLists(plData.price_lists || []);
      setProducts(prodData.products || []);
      setStores(storeData.stores || []);
    } catch {
      showToast('error', t('common:somethingWentWrong'));
    } finally {
      setLoading(false);
    }
  }, [orgId, t]);
  useEffect(() => { load(); }, [load]);

  // Load the selected list's size rows
  useEffect(() => {
    if (selected === 'default') { setOverrides({}); return; }
    organizationAPI.getPriceListPrices(orgId, selected as number).then(d => {
      setOverrides(sizeRowsById(d));
    }).catch(() => setOverrides({}));
  }, [selected, orgId]);

  /**
   * Οι τιμές efood ΤΗΣ ΟΜΑΔΑΣ. Μία κλήση ανά τιμοκατάλογο — `base` για τα
   * καταστήματα χωρίς τιμοκατάλογο (εκεί είναι τα 22 από τα 26 των Κουλουράδων).
   *
   * ΚΑΜΙΑ σιωπηλή αποτυχία με κόκκινο: 404 = ο server δεν έχει ακόμη τη Φ5β,
   * 403 = ο διακόπτης «επεξεργαστής καταλόγου» είναι κλειστός ή λείπει ο ρόλος.
   * Και στις δύο περιπτώσεις η στήλη ΔΕΝ υπάρχει και η οθόνη τιμών μένει ακριβώς
   * όπως ήταν — δεν είναι δουλειά της να εξηγήσει ρυθμίσεις πλατφόρμας.
   */
  const loadEfood = useCallback(async () => {
    setEfoodLoading(true);
    try {
      const data = await orgPlatformCatalogAPI.getPriceListPlatformPrices(
        orgId, selected === 'default' ? 'base' : (selected as number), EFOOD
      );
      const map: Record<string, PriceListPlatformRow> = {};
      (data.rows || []).forEach(r => { const k = priceListPlatformKey(r); if (k) map[k] = r; });
      const names: Record<number, string> = {};
      (data.stores || []).forEach(s => { if (s && s.store_id != null) names[Number(s.store_id)] = String(s.store_name || ''); });
      setEfood(map);
      setEfoodStoreNames(names);
      setEfoodMode(data.mode);
      setEfoodCanDecide(data.can_decide === true);
      setEfoodCanMap(data.can_map === true);
      // `available` = ΕΣΤΩ ΕΝΑ κατάστημα της ομάδας έχει εισαγμένο κατάλογο efood.
      // Χωρίς αυτό η στήλη θα ήταν 800 παύλες, δηλαδή θόρυβος πάνω στην οθόνη
      // που η Κασσιανή ανοίγει για να αλλάξει μία τιμή.
      setEfoodOn(data.available === true && data.mode !== 'off');
      setEfoodErr(null);
    } catch (err: any) {
      setEfood({});
      setEfoodOn(false);
      setEfoodCanDecide(false);
      setEfoodCanMap(false);
      setEfoodErr(
        (isCatalogRouteMissing(err) || isCatalogForbidden(err))
          ? null
          : (catalogErrorText(err) || 'Οι τιμές efood δεν φορτώθηκαν — οι τιμές του καταλόγου δεν επηρεάζονται.')
      );
    } finally {
      setEfoodLoading(false);
    }
  }, [orgId, selected]);

  useEffect(() => { loadEfood(); }, [loadEfood]);
  // Αλλαγή τιμοκαταλόγου = άλλη ομάδα καταστημάτων: ό,τι ήταν ανοιχτό αφορούσε
  // την προηγούμενη και θα έγραφε απόφαση με λάθος εικόνα μπροστά του.
  useEffect(() => { setPanel(null); setAsk(null); setOnlyEfoodDiff(false); }, [selected]);

  // The explicit channel value for the selected lens (null = inherits the base column)
  const explicitChan = useCallback((size: any, ck: 'ta' | 'di' | 'dl') => {
    if (selected === 'default') return size[SIZE[ck]];
    const ov = overrides[size.id];
    return ov ? ov[SIZE[ck]] : null;
  }, [selected, overrides]);

  // The effective base for the selected lens (override → catalog default). Used only
  // for DISPLAY (the inherited value shown as a placeholder).
  const effBase = useCallback((size: any) => {
    if (selected === 'default') return size[SIZE.base];
    const ov = overrides[size.id];
    return ov && ov[SIZE.base] != null ? ov[SIZE.base] : size[SIZE.base];
  }, [selected, overrides]);

  /**
   * Η ΙΣΧΥΟΥΣΑ τιμή ΚΑΝΑΛΙΟΥ όταν το κελί είναι κενό — αυτό που πράγματι
   * χρεώνει το κατάστημα, όχι η βάση. Ίδια σειρά με το backend της στήλης efood
   * (priceListPlatformPrices.js, `COALESCE(ov.price_modifier_delivery,
   * z.price_modifier_delivery, ov.price_modifier, z.price_modifier)`):
   * override καναλιού → ΚΑΤΑΛΟΓΟΣ καναλιού → override βάσης → βάση.
   *
   * ΓΙΑΤΙ ΔΕΝ ΑΡΚΕΙ Η ΒΑΣΗ: τιμοκατάλογος που αλλάζει ΜΟΝΟ τη βάση (2,20) ενώ ο
   * κατάλογος έχει Delivery 2,50 έδειχνε 2,20 στο Delivery, τη στιγμή που τα
   * καταστήματα χρεώνουν 2,50 — και η στήλη efood, που συγκρίνει με το 2,50, θα
   * έγραφε «ίδια» δίπλα σε κελί που λέει 2,20.
   */
  const effChan = useCallback((size: any, ck: 'ta' | 'di' | 'dl') => {
    const ov = selected === 'default' ? null : overrides[size.id];
    const chain = [ov ? ov[SIZE[ck]] : null, size[SIZE[ck]], ov ? ov[SIZE.base] : null, size[SIZE.base]];
    for (const v of chain) if (v != null && v !== '') return v;
    return null;
  }, [selected, overrides]);

  // The EXPLICIT base for the selected lens (null = inherits the catalog default).
  // Default lens = the catalog value itself; a named list = the override row's base,
  // or null when there is no override row — so an inherited base is saved as NULL and
  // never hardened into a fixed value that stops tracking Βασικός.
  const explicitBase = useCallback((size: any) => {
    if (selected === 'default') return size[SIZE.base];
    const ov = overrides[size.id];
    return ov ? ov[SIZE.base] : null;
  }, [selected, overrides]);

  // Rebuild the editable draft + linked/unlinked state whenever data or lens changes.
  // Μόνο μεγέθη: προϊόν χωρίς μέγεθος (τέλος, ή ανωμαλία) δεν έχει τιμή στον τιμοκατάλογο.
  useEffect(() => {
    const d: Record<string, Cell> = {};
    const u = new Set<string>();
    for (const p of products) {
      for (const s of (p.sizes || [])) {
        const k = ek(s.id, true);
        const base = explicitBase(s);
        const ta = explicitChan(s, 'ta');
        const di = explicitChan(s, 'di');
        const dl = explicitChan(s, 'dl');
        d[k] = { base: base != null ? String(base) : '', ta: ta != null ? String(ta) : '', di: di != null ? String(di) : '', dl: dl != null ? String(dl) : '' };
        if (ta != null || di != null || dl != null) u.add(k);
      }
    }
    setDraft(d);
    setUnlinked(u);
  }, [products, overrides, selected, explicitBase, explicitChan]);

  const num = (v: string) => (v === '' || v == null ? null : (parseFloat(v) || 0));

  /** Το κεντρικό προϊόν-γονέας ενός μεγέθους. */
  const parentOf = (size: any) => products.find(p => (p.sizes || []).some((z: any) => z.id === size.id));

  /**
   * Κλείδωμα γραμμής μεγέθους σε ονομαστικό τιμοκατάλογο (utils/priceListRowLock): μέγεθος
   * προϊόντος-τέλους — το ποσό του τέλους έρχεται από το state_fees, όχι από τη λίστα.
   */
  const rowLock = (size: any): PriceListRowLock => priceListRowLock({ lens: selected, product: parentOf(size) });
  const lockHelp = () => t('admin:priceListLocks.feeHelp');

  /**
   * Οι τιμές που είναι ΗΔΗ αποθηκευμένες στη γραμμή του τιμοκαταλόγου για ένα κλειδωμένο μέγεθος
   * (μόνο οι μη NULL), ως κείμενο. ΓΙΑΤΙ: το κλείδωμα δεν πρέπει να κρύβει παλιές γραμμές με τιμή —
   * μένουν στη βάση και ο χειριστής πρέπει να τις βλέπει για να τις καθαρίσει (F1-W5-10).
   */
  const storedListValues = (size: any): string[] => {
    const out: string[] = [];
    const base = explicitBase(size);
    if (base != null && base !== '') out.push(`${t('admin:priceListLocks.baseLabel')} ${Number(base).toFixed(2)}`);
    for (const ck of CHAN) {
      const v = explicitChan(size, ck);
      if (v != null && v !== '') out.push(`${CHAN_LABEL[ck]} ${Number(v).toFixed(2)}`);
    }
    return out;
  };

  /**
   * «Καθαρισμός — κληρονομεί» σε κλειδωμένο μέγεθος ΤΕΛΟΥΣ: όλες οι τιμές NULL (setSizeListPrice,
   * χωρίς περιορισμό στον server). Ποσό δεν αλλάζει: το τέλος χρεώνεται από το state_fees
   * (απόφαση 1_fees_source), όχι από το μέγεθος.
   */
  const canClearLocked = (size: any, lock: PriceListRowLock) =>
    canManage && lock === 'fee' && storedListValues(size).length > 0;

  const clearLockedRow = async (size: any) => {
    if (selected === 'default') return; // ο Βασικός δεν κλειδώνει ποτέ
    const prices: any = { [SIZE.base]: null, [SIZE.ta]: null, [SIZE.di]: null, [SIZE.dl]: null };
    try {
      await organizationAPI.setSizeListPrice(orgId, selected, size.id, prices);
      setOverrides(o => ({ ...o, [size.id]: { ...o[size.id], ...prices } }));
      showToast('success', t('admin:priceListLocks.cleared'));
    } catch (e: any) {
      showToast('error', e.response?.data?.error || t('common:somethingWentWrong'));
    }
  };

  /** Οι αποθηκευμένες τιμές της φακής (βάση + κανάλια· NULL = κληρονομεί) ίδιες με αυτές που θα σταλούν; */
  const samePrices = (size: any, prices: Record<string, number | null>) => {
    const key = (v: unknown) => (v === null || v === undefined || v === '' ? null : Math.round(Number(v) * 10000) / 10000);
    const saved: Record<string, unknown> = {
      [SIZE.base]: explicitBase(size), [SIZE.ta]: explicitChan(size, 'ta'),
      [SIZE.di]: explicitChan(size, 'di'), [SIZE.dl]: explicitChan(size, 'dl'),
    };
    return Object.values(SIZE).every(col => key(prices[col]) === key(saved[col]));
  };

  const saveSize = async (size: any, nextDraft: Cell, nextLinked: boolean) => {
    // Κλειδωμένη γραμμή: καμία κλήση — ένα κελί που δεν αλλάζει κανένα ποσό. Το πεδίο είναι
    // ήδη disabled· αυτό είναι δίχτυ ασφαλείας.
    if (rowLock(size)) return;
    // base: send NULL (not 0) when empty so a named-list row inherits Βασικός instead
    // of masking it with a hard 0, and so editing a channel never freezes the base.
    const prices: any = nextLinked
      ? { [SIZE.base]: num(nextDraft.base), [SIZE.ta]: null, [SIZE.di]: null, [SIZE.dl]: null }
      : { [SIZE.base]: num(nextDraft.base), [SIZE.ta]: num(nextDraft.ta), [SIZE.di]: num(nextDraft.di), [SIZE.dl]: num(nextDraft.dl) };
    // Ίδιες τιμές με τις αποθηκευμένες της φακής → καμία κλήση (20/10/2026: κάθε blur, ακόμη και χωρίς
    // αλλαγή, έστελνε «Ενημέρωση προϊόντος» στα καταστήματα). Ο server ελέγχει κι αυτός.
    if (samePrices(size, prices)) return;
    try {
      const res = await organizationAPI.setSizeListPrice(orgId, selected, size.id, prices);
      if (res && res.changed === false) return;
      // Reflect locally so display stays correct without a full reload
      if (selected === 'default') {
        setProducts(prev => prev.map(p => ({
          ...p, sizes: (p.sizes || []).map((z: any) => z.id === size.id ? { ...z, ...prices } : z),
        })));
      } else {
        setOverrides(o => ({ ...o, [size.id]: { ...o[size.id], ...prices } }));
      }
      // Πόσα καταστήματα πήραν αίτημα το λέει ο server (`stores`): τιμοκατάλογος χωρίς καταστήματα δεν
      // ειδοποιεί κανέναν — όχι «στάλθηκε αίτημα» στο κενό. Παλιός server χωρίς το πεδίο: όπως πριν.
      const stores = res && typeof res.stores === 'number' ? res.stores : null;
      showToast('success', selected === 'default' && lists.length === 0
        ? 'Αποθηκεύτηκε'
        : stores === 0
          ? 'Αποθηκεύτηκε — κανένα κατάστημα δεν έχει αυτόν τον τιμοκατάλογο'
          : stores != null ? `Στάλθηκε αίτημα σε ${stores} καταστήματα` : 'Στάλθηκε αίτημα στα καταστήματα');
      // Άλλαξε η τιμή μας → άλλαξε και η απάντηση στο «ίδια με το efood;». Χωρίς
      // αυτό η στήλη θα έδειχνε ακόμη ✓ πάνω στη ΝΕΑ τιμή, δηλαδή θα έλεγε ψέματα
      // ακριβώς τη στιγμή που η Κασσιανή περιμένει να δει τη διαφορά.
      if (efoodOn) loadEfood();
    } catch (e: any) {
      showToast('error', e.response?.data?.error || t('common:somethingWentWrong'));
    }
  };

  const onCellBlur = (size: any, forceLinked?: boolean) => {
    const k = ek(size.id, true);
    saveSize(size, draft[k], forceLinked !== undefined ? forceLinked : !unlinked.has(k));
  };

  const setCell = (k: string, col: keyof Cell, v: string) => setDraft(d => ({ ...d, [k]: { ...d[k], [col]: v } }));

  const toggleLink = (size: any) => {
    const k = ek(size.id, true);
    const wasUnlinked = unlinked.has(k);
    const nextLinked = wasUnlinked; // toggling: if it was unlinked, it becomes linked
    setUnlinked(s => { const n = new Set(s); n.has(k) ? n.delete(k) : n.add(k); return n; });
    if (nextLinked) {
      // Collapsing to one price → clear channel overrides (NULL) in the backend.
      saveSize(size, draft[k], true);
    }
  };

  // ---- στήλη efood: αναγνώσεις, αποφάσεις, αντιστοίχιση ----

  /**
   * Ποιος ΓΡΑΦΕΙ — το λέει ο ΙΔΙΟΣ ο server (`can_decide` / `can_map`), δεν το
   * μαντεύει η οθόνη από τον ρόλο. Ο διακόπτης σε 'read' αφήνει τη στήλη ορατή
   * (είναι πληροφορία που θέλει η Κασσιανή) αλλά κλειδώνει τις αποφάσεις: το
   * `PUT …/price-overrides` θα απαντούσε 403 ΑΦΟΥ ο χειριστής έχει ήδη επιλέξει.
   * Το `canManage` της οθόνης μένει ΚΑΙ αυτό — δύο ναι, ποτέ ένα.
   */
  const efoodCanAct = canManage && efoodCanDecide;
  const efoodCanMapNow = canManage && efoodCanMap && efoodMode !== 'read';
  const efoodRowOf = (id: number, isSize: boolean): PriceListPlatformRow | null => efood[ek(id, isSize)] || null;
  const efoodNoteworthy = (id: number, isSize: boolean) => isPlatformRowNoteworthy(efoodRowOf(id, isSize));
  /** Ένα προϊόν «θέλει προσοχή» όταν το ίδιο ή κάποιο μέγεθός του θέλει. */
  const productNoteworthy = (p: any): boolean => {
    if (efoodNoteworthy(p.id, false)) return true;
    const sizes = p.sizes || [];
    return sizes.some((s: any) => efoodNoteworthy(s.id, true));
  };

  /**
   * Η γραμμή του ΕΙΔΟΥΣ, όταν εκεί χρεώνει το efood — αυτό «βλέπει» μια γραμμή
   * ΜΕΓΕΘΟΥΣ που δεν έχει δική της τιμή efood.
   *
   * ΓΙΑΤΙ: το efood τιμολογεί στο είδος σε 148 από τις 169 γραμμές των
   * Κουλουράδων (μόνο 13 είναι `size_options`). Μέχρι χθες η γραμμή του
   * μεγέθους δεν έβρισκε τίποτα και έγραφε «—», δηλαδή «δεν υπάρχει στο
   * efood» — για είδος που το efood πουλάει κανονικά. Ο χειριστής το είδε στον
   * «ΦΥΣΙΚΟ ΧΥΜΟ ΠΟΡΤΟΚΑΛΙ (1 μέγεθος)»: η γραμμή του προϊόντος είχε τιμή και
   * τσιπ «κλειστό σε 7 καταστήματα», το «Κανονικό» από κάτω έγραφε «—».
   */
  const efoodItemLevelParent = (productId: number): PriceListPlatformRow | null => {
    const pr = efoodRowOf(productId, false);
    if (!pr || pr.state === 'absent' || pr.state === 'suggestion') return null;
    return rowPricesItemLevel(pr) ? pr : null;
  };

  /**
   * ΚΟΙΤΑΞΕ ΚΑΝΕΙΣ ΤΑ ΜΕΓΕΘΗ ΤΟΥ EFOOD ΓΙ' ΑΥΤΟ ΤΟ ΠΡΟΪΟΝ;
   *
   * Ο builder ζητά tiers/options για κάθε είδος ΜΕ ΜΕΓΕΘΗ (Φ5: η τιμή ζει μόνο
   * στο μέγεθος — priceListPlatformPrices.js, `sizePricedProductIds`). Όταν
   * ρωτήθηκε και ήρθε τιμή ΕΙΔΟΥΣ, το efood ΔΕΝ χρεώνει ανά μέγεθος και μπορούμε
   * να το πούμε. Το κατηγόρημα είναι ΤΟ ΙΔΙΟ με του backend, με τα δεδομένα που
   * έχει ήδη η οθόνη.
   */
  const efoodSawPlatformSizes = (productId: number): boolean => {
    const p = products.find((x: any) => x.id === productId);
    return ((p && p.sizes) || []).length > 0;
  };

  /**
   * ΜΕ ΤΙ ΣΥΓΚΡΙΘΗΚΕ, μέσα στο tooltip. Σε προϊόν ΕΝΟΣ μεγέθους η τιμή delivery
   * δεν είναι του προϊόντος — είναι του μεγέθους (147 από τα 246 προϊόντα των
   * Κουλουράδων έχουν `price_delivery` NULL στο προϊόν και την τιμή στο
   * μέγεθος). Το ✓ πάνω σε κελί Delivery που φαίνεται κενό θέλει εξήγηση,
   * αλλιώς διαβάζεται ως σφάλμα της οθόνης.
   */
  const deliveryBasisNote = (r: PriceListPlatformRow): string => {
    if (r.delivery_price_basis !== 'single_size') return '';
    const p = products.find((x: any) => x.id === r.org_product_id);
    const sizes = (p && p.sizes) || [];
    const byId = r.delivery_price_size_id != null
      ? sizes.find((z: any) => Number(z.id) === Number(r.delivery_price_size_id))
      : null;
    const z = byId || (sizes.length === 1 ? sizes[0] : null);
    const nm = z ? String(z.name || '').trim() : '';
    return nm
      ? ` — η τιμή Delivery του μεγέθους «${nm}»`
      : ' — η τιμή Delivery του μοναδικού μεγέθους';
  };
  const storeLabel = (id: number) => efoodStoreNames[id] || (stores.find((s: any) => s.id === id) || {}).name || `#${id}`;
  /** Μία τιμή, ή εύρος όταν τα καταστήματα της ομάδας δεν συμφωνούν. */
  const efoodPriceText = (r: PriceListPlatformRow): string => {
    if (r.efood_price != null) return fmt(r.efood_price);
    if (r.min_price != null && r.max_price != null) return `${fmt(r.min_price)} – ${fmt(r.max_price)}`;
    return '—';
  };
  /** Ποσά στη σειρά: «2,30 / 2,40» — ΕΝΑ € στο τέλος, όχι ένα ανά αριθμό. */
  const amounts = (values: number[]) =>
    values.map(v => new Intl.NumberFormat('el-GR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      .format(Number(v) || 0)).join(' / ');
  /**
   * Οι ΔΙΑΦΟΡΕΤΙΚΕΣ τιμές μιας διπλοεγγραφής, ταξινομημένες. Ίδιο φράγμα
   * ισότητας με το backend — δύο καταχωρήσεις στα 2,30 είναι ΜΙΑ τιμή, και η
   * στήλη δεν επιτρέπεται να γράψει «2,30 / 2,30» σαν να μαλώνουν.
   */
  const dupPrices = (r: PriceListPlatformRow, entries: PriceListPlatformDuplicateEntry[]): number[] => {
    const vals: number[] = [];
    const add = (v: number | null | undefined) => {
      if (v == null) return;
      const n = Number(v);
      if (!Number.isFinite(n)) return;
      if (!vals.some(x => Math.abs(x - n) < PLATFORM_PRICE_EPSILON)) vals.push(n);
    };
    entries.forEach(e => add(e.price));
    // Χωρίς ανάλυση ανά καταχώρηση μένει το εύρος της γραμμής: λιγότερη
    // πληροφορία, ποτέ ψεύτικη.
    if (vals.length === 0) { add(r.min_price); add(r.max_price); }
    return vals.sort((a, b) => a - b);
  };
  /** Η τιμή ΜΙΑΣ καταχώρησης· εύρος όταν τα καταστήματα δεν συμφωνούν σε αυτήν. */
  const dupEntryPriceText = (e: PriceListPlatformDuplicateEntry): string => {
    if (e.price != null) return fmt(e.price);
    if (e.min_price != null && e.max_price != null) return `${fmt(e.min_price)} – ${fmt(e.max_price)}`;
    return '—';
  };
  /**
   * ΤΟ ΕΥΡΟΣ ΤΗΣ ΓΡΑΜΜΗΣ ΔΕΝ ΕΙΝΑΙ ΟΙ ΤΙΜΕΣ ΤΩΝ ΔΙΔΥΜΩΝ. Όταν καμία καταχώρηση
   * δεν έχει ΜΙΑ τιμή (`price === null` παντού) και ο server λέει
   * `also_differs_between_stores`, το [min,max] της γραμμής είναι η διαφωνία
   * ΤΩΝ ΚΑΤΑΣΤΗΜΑΤΩΝ — όχι διαφορά ανάμεσα στις δύο κατηγορίες. Μετρημένο:
   * Γλυκά 2,30 & Νηστίσιμα 2,30 στο ένα κατάστημα, 2,40 & 2,40 στο άλλο· οι
   * δίδυμες συμφωνούν απόλυτα. Το «2,30 / 2,40» θα έστελνε την Κασσιανή να
   * διαλέξει ανάμεσα σε δύο κατηγορίες που δεν διαφωνούν καθόλου.
   */
  const dupRangeIsStores = (
    r: PriceListPlatformRow, entries: PriceListPlatformDuplicateEntry[]
  ): boolean => r.also_differs_between_stores === true
    && entries.length > 0 && entries.every(e => e.price == null);
  /**
   * ΤΟ ΟΝΟΜΑ ΜΙΑΣ ΚΑΡΤΗΣ — ΠΟΤΕ `master_key`. Το κλειδί είναι λεξιλόγιο της Φ5
   * («νηστισιμα#κουλουρι πραλινα#0») και το συμβόλαιο το λέει ρητά και από τις
   * δύο πλευρές (priceListPlatformPrices.js:757-762, platformCatalogApi.ts:838-840):
   * το `category_label` είναι το ανθρώπινο κείμενο, `null` όταν η κατηγορία
   * έλειπε από το στιγμιότυπο. Εφεδρεία: το όνομα efood, μετά «χωρίς κατηγορία».
   *
   * ΚΑΙ ΞΕΧΩΡΙΣΤΑ ΜΕΤΑΞΥ ΤΟΥΣ: δύο master του ΙΔΙΟΥ ονόματος κατηγορίας
   * (`γλυκα#…#0` και `γλυκα#…#1`) θα έδιναν δύο κάρτες που λένε και οι δύο
   * «Γλυκά» — δύο κουμπιά «κράτα την τιμή efood» χωρίς τρόπο να διαλέξεις.
   */
  const dupEntryLabels = (entries: PriceListPlatformDuplicateEntry[]): string[] => {
    const base = entries.map(e => (e.category_label || '').trim() || (e.name || '').trim() || 'χωρίς κατηγορία');
    const times = new Map<string, number>();
    base.forEach(b => times.set(b, (times.get(b) || 0) + 1));
    const seen = new Map<string, number>();
    return base.map((b, i) => {
      if ((times.get(b) || 0) < 2) return b;
      const n = (seen.get(b) || 0) + 1;
      seen.set(b, n);
      const nm = (entries[i].name || '').trim();
      return nm && nm !== b ? `${b} · ${nm}` : `${b} (${n})`;
    });
  };
  /**
   * ΚΛΕΙΣΤΟ = ΚΑΝΕΝΑΣ ΔΡΟΜΟΣ ΑΓΟΡΑΣ. Το `row.unavailable_stores` μετρά ΚΕΛΙΑ,
   * όχι καταστήματα — ο ίδιος ο builder το γράφει
   * (priceListPlatformPrices.js:1080-1086). Σε διπλοεγγραφή αυτό ψεύδεται:
   * μετρημένη «ΨΑΡΟΣΟΥΠΑ» σε ομάδα ΕΝΟΣ καταστήματος, «Πιάτα ημέρας» κλειστό
   * 15,20 και «Σούπες» ανοιχτό 13,20 → το κελί έγραφε «μη διαθέσιμο σε 1
   * κατάστημα» για ψαρόσουπα που ο πελάτης αγοράζει κανονικά.
   *
   * Η αλήθεια βγαίνει ΑΝΑ ΚΑΤΑΣΤΗΜΑ: κλειστό μετρά μόνο το κατάστημα που έχει
   * ΟΛΕΣ τις καταχωρήσεις του κλειστές. Μία κλειστή από τις δύο είναι
   * `partial` — λέγεται ήπια, χωρίς κόκκινο και χωρίς αριθμό καταστημάτων.
   */
  const dupUnavailable = (
    r: PriceListPlatformRow, entries: PriceListPlatformDuplicateEntry[]
  ): { closed: number | null; partial: boolean } => {
    const per = Array.isArray(r.per_store) ? r.per_store : [];
    if (per.length > 0) {
      const byStore = new Map<number, { open: number; shut: number }>();
      per.forEach(s => {
        const cur = byStore.get(s.store_id) || { open: 0, shut: 0 };
        if (s.is_available === false) cur.shut += 1; else cur.open += 1;
        byStore.set(s.store_id, cur);
      });
      let closed = 0;
      let partial = false;
      byStore.forEach(v => {
        if (v.shut > 0 && v.open === 0) closed += 1;
        else if (v.shut > 0) partial = true;
      });
      return { closed, partial };
    }
    // Χωρίς ανάλυση ανά κατάστημα μένει η ανά ΚΑΤΑΧΩΡΗΣΗ: «όλες κλειστές»
    // σημαίνει ότι όντως δεν πουλιέται — αλλά ΠΟΣΑ καταστήματα δεν το ξέρουμε,
    // και δεν το μαντεύουμε από κελιά.
    const shut = entries.filter(e => e.is_available === false).length;
    if (entries.length > 0 && shut === entries.length) return { closed: null, partial: false };
    return { closed: 0, partial: shut > 0 };
  };

  /**
   * ΤΟ ΕΥΡΟΣ ΤΗΣ ΑΠΟΦΑΣΗΣ, ρητά. Το `platform_catalog_price_overrides` έχει
   * κλειδί (organization_id, platform_name, master_key) — ΧΩΡΙΣ τιμοκατάλογο
   * (orgPlatformCatalog.js:2930-2938) — και το master_key είναι όνομα+κατηγορία,
   * κοινό σε ΟΛΗ την αλυσίδα (migration 225:133). Η οθόνη όμως είναι ολόκληρη
   * «ανά ομάδα»: φακοί, «Καταστήματα ανά τιμοκατάλογο», τιμές ανά κατάστημα.
   * Χωρίς να το πούμε, μια απόφαση από τον φακό «Κρήτη» (1 κατάστημα) παγώνει
   * σιωπηλά και τα άλλα 25.
   */
  const groupStoreCount = stores.filter(
    (s: any) => (s.price_list_id ?? null) === (selected === 'default' ? null : selected)
  ).length;
  const decisionWiderThanGroup = stores.length > 0 && groupStoreCount < stores.length;

  /**
   * Τα μεγέθη που ΣΥΜΠΑΡΑΣΥΡΟΝΤΑΙ. Το efood έχει ΕΝΑ κεντρικό είδος ανά προϊόν:
   * κάθε κελί μεγέθους φέρει το master_key ΤΟΥ ΕΙΔΟΥΣ
   * (priceListPlatformPrices.js:549-557), άρα απόφαση σε ένα μέγεθος = απόφαση
   * σε όλα. Το πάνελ λέγεται «Προϊόν · Μέγεθος» — αν δεν το γράψουμε, η
   * Κασσιανή διορθώνει το «Διπλό» και παγώνει και το «Μονό».
   */
  const siblingSizeNames = (row: PriceListPlatformRow): string[] => {
    const p = products.find((x: any) => x.id === row.org_product_id);
    return ((p && p.sizes) || []).map((z: any) => String(z.name || '')).filter(Boolean);
  };
  const productNameOf = (row: PriceListPlatformRow): string =>
    String((products.find((x: any) => x.id === row.org_product_id) || {}).name || '');

  const openEfoodPanel = (e: React.MouseEvent<HTMLElement>, key: string, label: string, row: PriceListPlatformRow) => {
    setPanel({ key, label, row, at: anchorOf(e.currentTarget) });
    setPanelMode((row.override && row.override.mode) || 'catalog');
    setPanelFixed(row.override && row.override.fixed_price != null ? String(row.override.fixed_price) : '');
    setDupFixedFor(null);
    setDupFixed('');
    setDupBusyKey(null);
    setPanelErr(null);
  };

  const openEfoodAsk = (
    e: React.MouseEvent<HTMLElement>, key: string, label: string,
    row: PriceListPlatformRow, productId: number, sizeId: number | null
  ) => {
    setAsk({ key, label, row, productId, sizeId, at: anchorOf(e.currentTarget) });
    setAskErr(null);
  };

  /**
   * Η μόνιμη απόφαση τιμής, ΕΝΑΣ δρόμος γραφής. Γράφεται με το ΥΠΑΡΧΟΝ
   * `PUT …/price-overrides` — το κλειδί είναι `master_key`, όχι `org_product_id`,
   * γι' αυτό το backend στέλνει τα master keys ανά γραμμή (και, στη
   * διπλοεγγραφή, ανά ΚΑΤΑΧΩΡΗΣΗ).
   *
   * Μία πόρτα για τη γραμμή ΚΑΙ για μία καταχώρηση: ένας έλεγχος ποσού, μία
   * προειδοποίηση αλυσίδας, ένα μήνυμα λάθους. Δύο αντίγραφα θα σήμαιναν δύο
   * συμπεριφορές για την ίδια ενέργεια.
   *
   * «Σταθερή τιμή» σε είδος με μεγέθη απορρίπτεται με 400 από το backend· ΔΕΝ
   * ξαναγράφουμε τον κανόνα εδώ — δείχνουμε το ελληνικό μήνυμά του μέσα στο πάνελ.
   */
  const writeEfoodDecision = async (keys: string[], mode: OverrideMode, fixedRaw: string): Promise<boolean> => {
    if (!panel) return false;
    if (keys.length === 0) {
      setPanelErr('Δεν ξέρουμε σε ποιο είδος efood αντιστοιχεί η γραμμή — η απόφαση δεν μπορεί να αποθηκευτεί.');
      return false;
    }
    const fixed = mode === 'fixed' ? parseFloat(fixedRaw) : null;
    // «Σταθερή» χωρίς ποσό δεν είναι γραμμή: το CHECK `pcpo_fixed_chk` (migration
    // 228) είναι αμφίδρομο και θα γύριζε 500 αντί για εξήγηση.
    if (mode === 'fixed' && (fixed === null || !Number.isFinite(fixed) || fixed <= 0)) {
      setPanelErr('Δώσε ποσό για τη σταθερή τιμή.');
      return false;
    }
    // Η απόφαση ΔΕΝ είναι του τιμοκαταλόγου — είναι της αλυσίδας (κλειδί
    // master_key, χωρίς price_list_id). Όταν ο φακός δείχνει ΛΙΓΟΤΕΡΑ
    // καταστήματα από όσα θα επηρεαστούν, ρωτάμε ΠΡΙΝ, όχι με toast μετά.
    if (decisionWiderThanGroup) {
      const sizesTouched = panel.row.org_size_id != null ? siblingSizeNames(panel.row) : [];
      const extra = sizesTouched.length > 1
        ? `\n\nΕπίσης, το efood κρατά ένα είδος ανά προϊόν: η απόφαση πιάνει ΟΛΑ τα μεγέθη του «${productNameOf(panel.row)}» (${sizesTouched.join(', ')}).`
        : '';
      const ok = window.confirm(
        `Η απόφαση ισχύει για ΟΛΑ τα ${stores.length} καταστήματα της αλυσίδας, όχι μόνο για τα ${groupStoreCount} αυτού του τιμοκαταλόγου.${extra}\n\nΣυνέχεια;`
      );
      if (!ok) return false;
    }
    const payload: PriceOverride[] = keys.map(mk => ({
      master_key: mk,
      mode,
      fixed_price: mode === 'fixed' ? (Number.isFinite(fixed as number) ? (fixed as number) : null) : null,
    }));
    try {
      setPanelBusy(true);
      setPanelErr(null);
      const rep = await orgPlatformCatalogAPI.putPriceOverrides(orgId, EFOOD, payload);
      if (rep.upserted === 0 && rep.deleted === 0 && rep.skipped.length > 0) {
        setPanelErr('Η απόφαση δεν αποθηκεύτηκε (άγνωστο κεντρικό είδος).');
        return false;
      }
      setPanel(null);
      showToast('success', mode === 'catalog'
        ? 'Επιστροφή στην προεπιλογή: το efood ακολουθεί την τιμή μας'
        : 'Η απόφαση αποθηκεύτηκε');
      loadEfood();
      return true;
    } catch (err: any) {
      setPanelErr(catalogErrorText(err) || 'Η απόφαση δεν αποθηκεύτηκε');
      return false;
    } finally {
      setPanelBusy(false);
    }
  };

  /** Η απόφαση ΤΗΣ ΓΡΑΜΜΗΣ (ένα κεντρικό είδος, ή τα ίδια master σε όλα τα κελιά). */
  const saveEfoodDecision = async () => {
    if (!panel) return;
    await writeEfoodDecision(rowMasterKeys(panel.row), panelMode, panelFixed);
  };

  /**
   * Η απόφαση ΜΙΑΣ καταχώρησης efood (διπλοεγγραφή). Γράφει ΜΟΝΟ το δικό της
   * `master_key`: «το νηστίσιμο κράτα το όπως είναι, το γλυκό πάρε τη δική μας»
   * είναι δύο διαφορετικές αποφάσεις και πρέπει να μπορούν να ειπωθούν χωριστά.
   * Το πάνελ κλείνει μετά την επιτυχία: τα δεδομένα ξαναφορτώνονται και ένα
   * ανοιχτό πάνελ θα έδειχνε την ΠΑΛΙΑ εικόνα δίπλα στη νέα απόφαση.
   */
  const saveEfoodEntryDecision = async (entry: PriceListPlatformDuplicateEntry, mode: OverrideMode, fixedRaw: string) => {
    setDupBusyKey(entry.master_key);
    try {
      await writeEfoodDecision([entry.master_key], mode, fixedRaw);
    } finally {
      setDupBusyKey(null);
    }
  };

  /** Κλικ σε κουμπί καταχώρησης: «σταθερή» θέλει ποσό ΠΡΙΝ γραφτεί, τα άλλα δύο όχι. */
  const onEntryDecision = (entry: PriceListPlatformDuplicateEntry, mode: OverrideMode) => {
    if (mode === 'fixed') {
      setDupFixedFor(entry.master_key);
      setDupFixed(entry.override && entry.override.fixed_price != null ? String(entry.override.fixed_price) : '');
      setPanelErr(null);
      return;
    }
    setDupFixedFor(null);
    saveEfoodEntryDecision(entry, mode, '');
  };

  /**
   * «Ναι, το ίδιο»: η γραμμή ονόματος της φάσης 1 γίνεται `mapped` με στόχο αυτό
   * το προϊόν. ΚΑΜΙΑ δεύτερη διαδρομή — γράφεται με το υπάρχον
   * `PUT …/platform-mappings/:id` (platformMappingApi.ts).
   *
   * ΜΟΝΟ ΣΕ ΕΠΙΠΕΔΟ ΠΡΟΪΟΝΤΟΣ: το backend χτίζει τις προτάσεις με kind
   * 'product' και size null (priceListPlatformPrices.js:694-700), άρα κελί
   * μεγέθους δεν φτάνει ποτέ εδώ. Δεν γράφουμε «στα τυφλά» `size_mode:'fixed'`
   * για ενδεχόμενο που δεν υπάρχει — λάθος μέγεθος σημαίνει λάθος τιμή σε κάθε
   * παραγγελία, και η επιλογή του θέλει άνθρωπο (οθόνη «Αντιστοιχίσεις»).
   */
  const confirmEfoodMapping = async () => {
    if (!ask || !efoodCanMapNow) return;
    const mappingId = ask.row.suggestion ? ask.row.suggestion.mapping_id : null;
    if (mappingId == null) {
      setAskErr('Λείπει η γραμμή αντιστοίχισης — κάν\' το από την οθόνη «Αντιστοιχίσεις».');
      return;
    }
    if (ask.sizeId != null) {
      setAskErr('Η αντιστοίχιση ονόματος σε συγκεκριμένο μέγεθος γίνεται από την οθόνη «Αντιστοιχίσεις».');
      return;
    }
    try {
      setAskBusy(true);
      setAskErr(null);
      const body: any = { org_product_id: ask.productId, status: 'mapped', target_kind: 'product' };
      await orgPlatformMappingsAPI.update(orgId, Number(mappingId), body);
      setAsk(null);
      showToast('success', 'Αντιστοιχίστηκε');
      loadEfood();
    } catch (err: any) {
      setAskErr(catalogErrorText(err) || 'Η αντιστοίχιση δεν αποθηκεύτηκε');
    } finally {
      setAskBusy(false);
    }
  };

  // ---- price-list CRUD ----
  const submitModal = async () => {
    if (!modal) return;
    const name = modal.name.trim();
    if (!name) { showToast('error', 'Δώσε όνομα'); return; }
    try {
      setModalBusy(true);
      if (modal.mode === 'create') {
        // Copy an existing list's prices when chosen; otherwise start as a pure lens (no override
        // rows, so every price inherits Βασικός until changed — avoids a frozen snapshot).
        const payload = modal.copyFromId
          ? { name, copy_from_id: modal.copyFromId }
          : { name, copy_from_default: false };
        const res = await organizationAPI.createPriceList(orgId, payload);
        await load();
        setSelected(res.price_list.id);
        const src = modal.copyFromId ? lists.find(l => l.id === modal.copyFromId)?.name : null;
        showToast('success', src ? `Τιμοκατάλογος δημιουργήθηκε — αντίγραφο του «${src}»` : 'Τιμοκατάλογος δημιουργήθηκε — κληρονομεί τον Βασικό');
      } else {
        await organizationAPI.updatePriceList(orgId, modal.id!, { name });
        await load();
        showToast('success', 'Μετονομάστηκε');
      }
      setModal(null);
    } catch (e: any) {
      showToast('error', e.response?.data?.error || t('common:somethingWentWrong'));
    } finally {
      setModalBusy(false);
    }
  };

  // Reassign a store to a price list (or back to Βασικός), updating counts locally.
  const assignStore = async (storeId: number, listId: number | 'default') => {
    const pid = listId === 'default' ? null : Number(listId);
    try {
      await organizationAPI.assignStorePriceList(orgId, storeId, pid);
      const next = stores.map(s => s.id === storeId ? { ...s, price_list_id: pid } : s);
      setStores(next);
      setLists(prev => prev.map(l => ({ ...l, store_count: next.filter(s => (s.price_list_id ?? null) === l.id).length })));
      showToast('success', 'Ο τιμοκατάλογος του καταστήματος ενημερώθηκε');
    } catch (e: any) {
      showToast('error', e.response?.data?.error || t('common:somethingWentWrong'));
    }
  };
  const handleDelete = async (list: PriceList) => {
    const warn = list.store_count > 0 ? `\n\n⚠️ ${list.store_count} καταστήματα τη χρησιμοποιούν — θα πέσουν στον Βασικό.` : '';
    if (!window.confirm(`Διαγραφή «${list.name}»;${warn}`)) return;
    try {
      await organizationAPI.deletePriceList(orgId, list.id);
      if (selected === list.id) setSelected('default');
      await load();
      showToast('success', 'Διαγράφηκε');
    } catch (e: any) { showToast('error', e.response?.data?.error || t('common:somethingWentWrong')); }
  };

  const handleBulk = async () => {
    if (selected === 'default') return;
    const pct = parseFloat(bulkPct);
    if (!Number.isFinite(pct)) { showToast('error', 'Δώσε ποσοστό'); return; }
    const cur = lists.find(l => l.id === selected);
    if (!window.confirm(`Όλος ο τιμοκατάλογος «${cur?.name}» θα οριστεί = Βασικός ${pct >= 0 ? '+' : ''}${pct}%, αντικαθιστώντας ΟΛΕΣ τις τρέχουσες τιμές του. Συνέχεια;`)) return;
    try {
      setBulkBusy(true);
      const res = await organizationAPI.bulkSetPriceList(orgId, selected as number, pct);
      const d = await organizationAPI.getPriceListPrices(orgId, selected as number);
      setOverrides(sizeRowsById(d));
      showToast('success', res.message || 'Εφαρμόστηκε');
    } catch (e: any) {
      showToast('error', e.response?.data?.error || t('common:somethingWentWrong'));
    } finally {
      setBulkBusy(false);
    }
  };

  const searched = products.filter(p => !search || p.name.toLowerCase().includes(search.toLowerCase()));
  /**
   * Πόσες ΓΡΑΜΜΕΣ (προϊόν ή μέγεθος) θέλουν προσοχή. Μετράει πάνω στην αναζήτηση
   * που βλέπει ο χρήστης — ένας μετρητής «12» με 2 γραμμές στην οθόνη δεν βοηθά.
   */
  let efoodNoteCount = 0;
  if (efoodOn) {
    for (let i = 0; i < searched.length; i++) {
      // Και η γραμμή του ΙΔΙΟΥ του προϊόντος μετρά: ένα είδος με μεγέθη μπορεί
      // να έχει πρόταση/διαφορά στο επίπεδό του (φαίνεται στη γραμμή-κεφαλίδα).
      if (efoodNoteworthy(searched[i].id, false)) efoodNoteCount++;
      const sizes = searched[i].sizes || [];
      // Η ΓΡΑΜΜΗ-ΚΑΘΡΕΦΤΗΣ ΤΟΥ ΜΟΝΑΔΙΚΟΥ ΜΕΓΕΘΟΥΣ ΔΕΝ ΜΕΤΡΑ ΔΕΥΤΕΡΗ ΦΟΡΑ: λέει
      // ΤΟ ΙΔΙΟ με τη γραμμή του προϊόντος (147 τέτοια προϊόντα στους
      // Κουλουράδες). Τον κανόνα τον κρατά το `isPlatformRowNoteworthy`, ένα
      // σημείο με το `needsAttention` του backend — εδώ δεν ξαναγράφεται.
      for (let j = 0; j < sizes.length; j++) { if (efoodNoteworthy(sizes[j].id, true)) efoodNoteCount++; }
    }
  }
  const filtered = (onlyEfoodDiff && efoodOn) ? searched.filter(productNoteworthy) : searched;
  const isDefault = selected === 'default';

  // Only show the channels the org's stores actually use (e.g. hide Dine-in if no
  // store offers it). With no stores yet, show all so a fresh org can pre-configure.
  const orgChannels: Record<'ta' | 'di' | 'dl', boolean> = stores.length === 0
    ? { ta: true, di: true, dl: true }
    : {
        ta: stores.some((s: any) => s.takeaway !== false),
        di: stores.some((s: any) => s.dine_in === true),
        // delivery via self OR platform (efood/wolt)
        dl: stores.some((s: any) => s.delivery_self === true || s.delivery_efood === true || s.delivery_wolt === true),
      };
  const activeChan = CHAN.filter(c => orgChannels[c]);
  const multiChannel = activeChan.length > 1;       // show channel columns + link toggle
  const totalCols = 1 + (multiChannel ? 1 + activeChan.length : 1) + (efoodOn ? 1 : 0);

  // ── Το ανοιχτό πάνελ όταν η γραμμή είναι ΔΙΠΛΟΕΓΓΡΑΦΗ ────────────────────
  // Μία απόφαση δεν αρκεί: υπάρχουν ΔΥΟ κεντρικά είδη efood και το `master_key`
  // της γραμμής είναι `null`. Η λίστα των καταχωρήσεων ΕΙΝΑΙ ο μηχανισμός
  // απόφασης εδώ — χωρίς αυτήν το πάνελ θα άνοιγε και δεν θα μπορούσε να γράψει.
  const panelIsDup = !!panel && panel.row.state === 'duplicate_on_platform';
  const panelDupEntries = panel ? rowDuplicateEntries(panel.row) : [];
  /**
   * Τα ΟΝΟΜΑΤΑ των καταχωρήσεων, υπολογισμένα ΜΙΑ φορά για όλη τη λίστα: οι
   * κάρτες, τα aria-label των κουμπιών, το πεδίο ποσού και η ανά-κατάστημα
   * λίστα λένε ΤΟ ΙΔΙΟ όνομα — και ποτέ το `master_key`.
   */
  const panelDupLabels = dupEntryLabels(panelDupEntries);
  /** master_key → ανθρώπινο όνομα, για να ονομάζεται και η ανά-κατάστημα λίστα. */
  const panelDupCategory = (mk: string | null | undefined): string => {
    if (!mk) return '';
    const i = panelDupEntries.findIndex(x => x.master_key === mk);
    return i >= 0 ? panelDupLabels[i] : '';
  };

  if (loading) {
    // Σκελετός γραμμών (όπως ο «Πίνακας»), όχι σκέτο spinner: η σελίδα δεν «πηδά» όταν έρθουν οι τιμές.
    return (
      <ListPage embedded title={t('orgPages:priceLists.title')} testId="org-pricelists">
        <div role="status" className="space-y-4 py-6" data-testid="pricelists-loading">
          <span className="sr-only">{t('orgPages:priceLists.loading')}</span>
          {[46, 72, 58, 80, 40, 66].map((w, i) => (
            <span key={i} aria-hidden="true" className="lp-skel" style={{ width: `${w}%` }} />
          ))}
        </div>
      </ListPage>
    );
  }

  /**
   * Το κελί efood. Τέσσερις καταστάσεις — και η τέταρτη («;») είναι αυτή που
   * κάνει την αντιστοίχιση να πάψει να είναι ξεχωριστή δουλειά: απαντιέται με
   * ναι/όχι μέσα στον τιμοκατάλογο που η Κασσιανή ήδη ξέρει.
   */
  const efoodInline = (entity: any, isSize: boolean, label: string, productId: number) => {
    const k = ek(entity.id, isSize);
    const r = efood[k];
    // Στο πάνελ/διάλογο το σκέτο «Μεγάλο» δεν λέει τίποτα: γράφουμε «Προϊόν · Μεγάλο».
    const full = isSize
      ? `${((products.find((p: any) => p.id === productId) || {}).name || '')} · ${label}`.replace(/^ · /, '')
      : label;
    /**
     * ΤΟ ΜΕΓΕΘΟΣ ΠΟΥ ΤΟ ΕΙΔΟΣ ΤΟΥ ΥΠΑΡΧΕΙ ΣΤΟ EFOOD ΔΕΝ ΓΡΑΦΕΙ «ΔΕΝ ΥΠΑΡΧΕΙ».
     *
     * Το efood χρεώνει στο ΕΙΔΟΣ (148 από τις 169 γραμμές των Κουλουράδων), άρα
     * για τη γραμμή του μεγέθους δεν έρχεται — ούτε πρέπει να έρθει — δική της
     * τιμή. Το «—» εκεί σημαίνει «δεν υπάρχει στο efood» και είναι ψέμα: ο
     * χειριστής το είδε στον «ΦΥΣΙΚΟ ΧΥΜΟ ΠΟΡΤΟΚΑΛΙ», όπου η γραμμή του
     * προϊόντος είχε τιμή ΚΑΙ τσιπ «κλειστό σε 7 καταστήματα» και το «Κανονικό»
     * από κάτω έγραφε παύλα.
     *
     * ΚΑΜΙΑ ΕΤΥΜΗΓΟΡΙΑ ΕΔΩ: η τιμή που δείχνουμε είναι του ΕΙΔΟΥΣ, όχι αυτού του
     * μεγέθους. Σε προϊόν με πολλά μεγέθη η σύγκριση «με ποιο μέγεθος;» δεν έχει
     * απάντηση — και μια τυχαία θα έβαφε κίτρινα προϊόντα που συμφωνούν.
     */
    const itemLevelUp = isSize && (!r || r.state === 'absent') ? efoodItemLevelParent(productId) : null;
    /**
     * ΓΡΑΜΜΗ-ΚΑΘΡΕΦΤΗΣ: το μοναδικό μέγεθος που δείχνει τα κελιά ΤΟΥ ΓΟΝΕΑ του.
     * Ό,τι γράφει είναι ΤΟ ΙΔΙΟ γεγονός με τη γραμμή από πάνω — γι' αυτό παρακάτω
     * του επιτρέπεται ΜΟΝΟ η ετυμηγορία τιμής (βλ. τον κλάδο «Ο ΚΑΘΡΕΦΤΗΣ ΔΙΚΑΙΟΥΤΑΙ…»).
     */
    const mirror = !!r && r.mirrors_product === true;
    // ΤΑ ΤΣΙΠ ΧΤΙΖΟΝΤΑΙ ΠΡΙΝ ΑΠΟ ΚΑΘΕ ΚΛΑΔΟ ΕΞΟΔΟΥ. Ο κλάδος «η τιμή είναι στο
    // προϊόν» έβγαινε από πάνω τους και τα πετούσε: σήμερα δεν χάνεται τίποτα
    // (γραμμή `absent` δεν έχει κελιά, άρα ούτε κλειστά ούτε απόφαση), αύριο
    // όμως η πρώτη αλλαγή του builder που θα δώσει σε τέτοια γραμμή μετρητή
    // κλειστών θα τον κατάπινε σιωπηλά — ΑΚΡΙΒΩΣ το τσιπ για το οποίο άνοιξε
    // αυτό το θέμα («κλειστό σε 7 καταστήματα»).
    const unav = Number(r && r.unavailable_stores) || 0;
    // Ο ΚΑΘΡΕΦΤΗΣ ΔΕΝ ΞΑΝΑΛΕΕΙ ΤΑ ΤΣΙΠ ΤΟΥ ΓΟΝΕΑ: η γραμμή-κεφαλίδα δείχνει ΠΑΝΤΑ
    // το κελί efood όταν υπάρχει κλειστό ή απόφαση, άρα «🚫 7» και στις δύο
    // γραμμές διαβάζεται 14, και δύο «κρ» σαν δύο αποφάσεις για ΕΝΑ master_key.
    const unavChip = unav > 0 && !mirror ? (
      <span title={`Μη διαθέσιμο σε ${unav} ${unav === 1 ? 'κατάστημα' : 'καταστήματα'} του efood`}
        className="ml-1 inline-flex items-center gap-0.5 text-[10px] text-red-700 dark:text-red-300 align-middle">
        <EyeOff className="h-3 w-3" />{unav}
      </span>
    ) : null;
    const decision = r && r.override ? r.override.mode : 'catalog';
    const decisionChip = mirror ? null : (decision === 'keep_platform'
      ? <span title="Απόφαση: κράτα την τιμή efood" className="ml-1 text-[10px] text-t3">κρ</span>
      : decision === 'fixed'
        ? <span title="Απόφαση: σταθερή τιμή" className="ml-1 text-[10px] text-t3">στ</span>
        : null);
    /**
     * «Η ΤΙΜΗ EFOOD ΕΙΝΑΙ ΣΤΟ ΠΡΟΪΟΝ» — και τίποτα παραπάνω από όσα ξέρουμε.
     *
     * Το `price_basis: 'item'` λέει «ο αριθμός ΑΥΤΗΣ της γραμμής ήρθε από το
     * είδος του efood», ΟΧΙ «το efood δεν έχει τιμές ανά μέγεθος». Το δεύτερο
     * το λέμε ΜΟΝΟ όταν ο builder όντως ρώτησε για επιλογές μεγέθους (βλ.
     * `efoodSawPlatformSizes`)· αλλιώς μια σίγουρη πρόταση θα έλεγε στην Κασσιανή
     * να πάψει να ψάχνει τιμή που ίσως υπάρχει.
     */
    const itemLevelText = (p: number | null | undefined, sure: boolean) => (
      <span className="text-[10px] text-t4"
        title={sure
          ? `Το efood χρεώνει μία τιμή για όλο το είδος, όχι ανά μέγεθος${p != null ? ` (${fmt(p)})` : ''} — η σύγκριση γίνεται στη γραμμή του προϊόντος.`
          : `Η τιμή efood${p != null ? ` (${fmt(p)})` : ''} φαίνεται στη γραμμή του προϊόντος — αν το efood έχει χωριστή τιμή γι' αυτό το μέγεθος, δεν τη γνωρίζουμε.`}>
        {p != null ? `στο προϊόν ${fmt(p)}` : 'η τιμή efood είναι στο προϊόν'}
      </span>
    );
    if (itemLevelUp) {
      return <>{itemLevelText(itemLevelUp.efood_price, efoodSawPlatformSizes(productId))}{unavChip}{decisionChip}</>;
    }
    // ΑΠΟΥΣΙΑ ΓΡΑΜΜΗΣ = `default_state` του συμβολαίου ('absent'): δεν υπάρχει
    // σελιδοποίηση, άρα «δεν ήρθε γραμμή» σημαίνει «δεν πουλιέται στο efood».
    if (!r) {
      return <span className="text-t4" title="δεν υπάρχει στο efood">—</span>;
    }
    /**
     * Ο ΚΑΘΡΕΦΤΗΣ ΔΙΚΑΙΟΥΤΑΙ ΜΟΝΟ ΤΗΝ ΕΤΥΜΗΓΟΡΙΑ ΤΙΜΗΣ.
     *
     * Η γραμμή του μοναδικού μεγέθους παίρνει ΤΑ ΙΔΙΑ κελιά με τον γονέα
     * (priceListPlatformPrices.js:1328-1356): ίδιο `state`, ίδιες
     * `duplicate_entries`, ίδια `master_keys`. Το «✓ 2,50» της είναι χρήσιμο —
     * εκεί δίπλα ζει το κελί Delivery που θα αλλάξει. Ένα δεύτερο «2 φορές στο
     * efood» ή «διαφορετικές» όμως είναι ΤΟ ΙΔΙΟ κουμπί για τα ΙΔΙΑ master_keys:
     * ο μετρητής λέει «1», η οθόνη δείχνει δύο. Εδώ λέμε μόνο ΠΟΥ ζει η απόφαση.
     */
    if (mirror && (r.efood_price == null || r.state !== 'match')) {
      const p = r.efood_price;
      return (
        <span className="text-[10px] text-t4"
          title={`Η τιμή efood${p != null ? ` (${fmt(p)})` : ''} φαίνεται στη γραμμή του προϊόντος — εκεί είναι και η απόφαση.`}>
          {p != null ? `στο προϊόν ${fmt(p)}` : 'η τιμή efood είναι στο προϊόν'}
        </span>
      );
    }

    if (r.state === 'absent') {
      return <><span className="text-t4" title="δεν υπάρχει στο efood">—</span>{unavChip}</>;
    }
    if (r.state === 'suggestion') {
      const s = r.suggestion;
      return (
        <button onClick={e => openEfoodAsk(e, k, full, r, productId, isSize ? entity.id : null)}
          title={`Το efood έχει «${s ? s.display_name : ''}»${s && s.stores ? ` σε ${s.stores} καταστήματα` : ''} — είναι το ίδιο προϊόν;`}
          className="max-w-[9rem] truncate px-1.5 py-1 rounded-lg border border-amber-500/40 bg-amber-500/10 text-amber-800 dark:text-amber-300 text-[11px] hover:bg-amber-500/20">
          ? {s ? s.display_name : 'όνομα efood'}
        </button>
      );
    }
    /**
     * ΔΙΠΛΟΕΓΓΡΑΦΗ: το ΙΔΙΟ προϊόν είναι καταχωρημένο δύο (ή τρεις) φορές στο
     * efood, ΜΕΣΑ στο ίδιο κατάστημα, σε διαφορετικές κατηγορίες. Μέχρι χθες
     * αυτές οι γραμμές έγραφαν «διαφορετικές» — δηλαδή «τα καταστήματα δεν
     * συμφωνούν» — για ΕΝΑ κατάστημα (μετρημένο: «ΣΟΛΟΜΟΣ», δύο master στο
     * store_id 4). Ψέμα σε ~10 από τις ~179 γραμμές κάθε τιμοκαταλόγου.
     *
     * ΤΟ ΚΕΙΜΕΝΟ ΛΕΕΙ ΤΟ ΓΕΓΟΝΟΣ, ΟΧΙ ΤΟ ΣΥΜΠΕΡΑΣΜΑ: «2 φορές στο efood ·
     * 2,30 / 2,40». Όταν οι δίδυμες έχουν την ΙΔΙΑ τιμή (η συνηθισμένη
     * περίπτωση στους Κουλουράδες) το γράφουμε ρητά ως «ίδια τιμή» και ΧΩΡΙΣ
     * κίτρινο: δεν υπάρχει διαφωνία, υπάρχει δεύτερη βιτρίνα.
     */
    if (r.state === 'duplicate_on_platform') {
      const entries = rowDuplicateEntries(r);
      const times = rowDuplicateCount(r) || 2;
      const vals = dupPrices(r, entries);
      const rangeIsStores = dupRangeIsStores(r, entries);
      // ΚΑΠΟΙΟ ΚΑΤΑΣΤΗΜΑ ΤΟ ΕΧΕΙ ΧΩΡΙΣ ΤΙΜΗ (0,00) ΕΝΩ ΑΛΛΑ ΤΟ ΧΡΕΩΝΟΥΝ. Σε
      // κάθε άλλη κατάσταση αυτό ΕΙΝΑΙ «διαφωνία» (το backend το βάζει στο
      // `differs_between_stores`)· η διπλοεγγραφή δεν το ακυρώνει, αλλιώς το
      // κελί θα έγραφε «ίδια τιμή 2,30 €» για γραμμή όπου ένα κατάστημα δεν
      // χρεώνει τίποτα, και η γραμμή θα έβγαινε από το «Μόνο διαφορές efood».
      const gap = r.duplicate_price_gap === true;
      // «Διαφορετική τιμή» = ό,τι λέει ο server για ΜΕΣΑ στο κατάστημα, ή ό,τι
      // βλέπουν τα μάτια (πάνω από μία διακριτή τιμή ΤΩΝ ΚΑΤΑΧΩΡΗΣΕΩΝ). Το
      // εύρος της γραμμής δεν μετρά: εκεί μαλώνουν τα καταστήματα, όχι οι δίδυμες.
      const disagree = r.duplicate_prices_differ === true || gap || (!rangeIsStores && vals.length > 1);
      const cats = dupEntryLabels(entries).join(' · ');
      const priceBit = vals.length === 0
        // ΕΙΔΟΣ ΜΕ ΜΕΓΕΘΗ ΠΟΥ ΕΙΝΑΙ ΚΑΙ ΔΙΠΛΟΓΡΑΜΜΕΝΟ: το efood δεν έχει τιμή
        // προϊόντος (όλες οι καταχωρήσεις έρχονται με `price: null`), οπότε ο
        // κλάδος «ανά μέγεθος» παρακάτω δεν φτάνεται πια — η διπλοεγγραφή
        // προηγείται. Σκέτο «2 φορές στο efood», χωρίς αριθμό, θα διαβαζόταν
        // «το efood δεν μου λέει τιμή» εκεί που οι τιμές είναι στις γραμμές από
        // κάτω. Λέμε ΚΑΙ ΤΑ ΔΥΟ.
        ? (r.price_basis === 'size_options' ? 'ανά μέγεθος' : '')
        : rangeIsStores
          ? `τα καταστήματα διαφωνούν (${amounts(vals)} €)`
          : (vals.length > 1
            ? `${amounts(vals)} €`
            // «ίδια τιμή» ΜΟΝΟ όταν όντως συμφωνούν ΟΛΑ. Με κενό τιμής η μία
            // αριθμητική τιμή δεν είναι η τιμή όλων — τη γράφουμε σκέτη.
            : (gap ? fmt(vals[0]) : `ίδια τιμή ${fmt(vals[0])}`));
      const decided = entries.filter(x => x.override && x.override.mode && x.override.mode !== 'catalog').length;
      const loud = isPlatformRowNoteworthy(r);
      // ΤΟ ΤΣΙΠ «ΚΛΕΙΣΤΟ» ΒΓΑΙΝΕΙ ΑΠΟ ΤΙΣ ΚΑΤΑΧΩΡΗΣΕΙΣ, όχι από τη γραμμή —
      // το `unavChip` μετρά κελιά και σε διπλοεγγραφή λέει «δεν πουλιέται» εκεί
      // που πουλιέται (δες `dupUnavailable`).
      const shut = dupUnavailable(r, entries);
      const dupUnavChip = shut.closed === null || (shut.closed || 0) > 0 ? (
        <span title={shut.closed === null
          ? 'Μη διαθέσιμο στο efood — καμία από τις καταχωρήσεις δεν είναι ανοιχτή'
          : `Μη διαθέσιμο σε ${shut.closed} ${shut.closed === 1 ? 'κατάστημα' : 'καταστήματα'} του efood — εκεί καμία από τις καταχωρήσεις δεν είναι ανοιχτή`}
          className="ml-1 inline-flex items-center gap-0.5 text-[10px] text-red-700 dark:text-red-300 align-middle">
          <EyeOff className="h-3 w-3" />{shut.closed === null ? '' : shut.closed}
        </span>
      ) : shut.partial ? (
        <span title="Μία από τις καταχωρήσεις efood είναι κλειστή — το προϊόν πουλιέται από την άλλη"
          className="ml-1 inline-flex items-center gap-0.5 text-[10px] text-t3 align-middle">
          <EyeOff className="h-3 w-3" />
        </span>
      ) : null;
      return (
        <>
          <button onClick={e => openEfoodPanel(e, k, full, r)}
            title={`Το ίδιο προϊόν είναι καταχωρημένο ${times} φορές στο efood${cats ? ` (${cats})` : ''}`
              + (disagree ? ' — με διαφορετική τιμή. Διάλεξε τι ισχύει για καθεμιά.' : ', με την ίδια τιμή.')
              + (gap ? ' Κάποιο κατάστημα το έχει χωρίς τιμή (0,00) — άνοιξέ το για να δεις ποιο.' : '')
              + (r.also_differs_between_stores === true
                ? ' Τα καταστήματα της ομάδας δεν χρεώνουν το ίδιο — άνοιξέ το για τη λίστα ανά κατάστημα.' : '')}
            className={`px-1.5 py-1 rounded-lg text-[11px] ${loud
              ? 'border border-amber-500/40 bg-amber-500/10 text-amber-800 dark:text-amber-300 font-medium hover:bg-amber-500/20'
              : 'border border-line text-t2 hover:text-t1 hover:bg-surface-3'}`}>
            {times} φορές στο efood{priceBit ? ` · ${priceBit}` : ''}
          </button>
          {decided > 0 && (
            <span className="ml-1 text-[10px] text-t3"
              title={`${decided} από τις ${entries.length} καταχωρήσεις efood έχουν απόφαση τιμής`}>απόφ.</span>
          )}
          {dupUnavChip}
        </>
      );
    }
    if (r.state === 'differs_between_stores') {
      const range = (r.min_price != null && r.max_price != null) ? `${fmt(r.min_price)} – ${fmt(r.max_price)}` : '';
      return (
        <>
          <button onClick={e => openEfoodPanel(e, k, full, r)}
            title={range ? `Τιμές efood: ${range}` : 'Τα καταστήματα αυτού του τιμοκαταλόγου δεν έχουν την ίδια τιμή efood'}
            className="px-1.5 py-1 rounded-lg border border-amber-500/40 bg-amber-500/10 text-amber-800 dark:text-amber-300 text-[11px] font-medium hover:bg-amber-500/20">
            διαφορετικές
          </button>
          {decisionChip}
          {unavChip}
        </>
      );
    }
    // ΕΙΔΟΣ ΜΕ ΜΕΓΕΘΗ: το efood ΔΕΝ έχει τιμή προϊόντος — οι αριθμοί είναι στα
    // μεγέθη από κάτω. Ένα «—» εδώ θα διαβαζόταν «δεν υπάρχει στο efood».
    if (r.efood_price == null) {
      return (
        <>
          <span className="text-[10px] text-t4"
            title={r.price_basis === 'size_options' ? 'Η τιμή efood ορίζεται στα μεγέθη' : 'Χωρίς τιμή στο efood'}>
            {r.price_basis === 'size_options' ? 'ανά μέγεθος' : '—'}
          </span>
          {unavChip}
        </>
      );
    }
    // ΤΡΕΙΣ καταστάσεις, ΟΧΙ δύο. Το `same_as_delivery` είναι tri-state: `null`
    // σημαίνει «δεν συγκρίθηκε» (λείπει η ΔΙΚΗ ΜΑΣ τιμή delivery) — ΟΧΙ «ίδια».
    // Ένα ✓ εκεί θα έγραφε «μην ασχοληθείς» πάνω σε προϊόν που το efood χρεώνει
    // και εμείς δεν τιμολογούμε καθόλου· η αποστολή αργότερα το απορρίπτει με
    // 'zero_price' και κανείς δεν ξέρει γιατί. Μόνο το ρητό `true` βάζει ✓.
    const cmp = r.same_as_delivery;
    const same = cmp === true;
    const differs = cmp === false;
    /**
     * ΤΟ ΤΕΤΑΡΤΟ «ΔΕΝ ΣΥΓΚΡΙΝΕΤΑΙ»: το efood χρεώνει ΜΙΑ τιμή για όλο το είδος,
     * εμείς τιμολογούμε σε ΠΟΛΛΑ μεγέθη. Δεν υπάρχει μία δική μας τιμή για να
     * συγκριθεί — και το «χωρίς Delivery» θα ήταν ψέμα (τιμή έχουμε, σε κάθε
     * μέγεθος). Λέγεται ρητά, ώστε η Κασσιανή να κοιτάξει τα μεγέθη από κάτω
     * αντί να ψάχνει τιμή που «λείπει».
     */
    const sizesAmbiguous = cmp == null && r.no_compare_reason === 'multi_size_item_price';
    // Με ΤΙ συγκρίθηκε: σε προϊόν ΕΝΟΣ μεγέθους η τιμή είναι του μεγέθους, όχι
    // του προϊόντος — το κελί Delivery δίπλα φαίνεται κενό και το ✓ χρειάζεται
    // εξήγηση, αλλιώς διαβάζεται ως σφάλμα της οθόνης.
    const basisNote = deliveryBasisNote(r);
    return (
      <>
        <button onClick={e => openEfoodPanel(e, k, full, r)}
          title={same
            ? `Ίδια με την τιμή Delivery${basisNote}`
            : differs
              ? `Διαφέρει από το Delivery${r.delivery_price != null ? ` (${fmt(r.delivery_price)})` : ''}${basisNote}`
              : sizesAmbiguous
                ? 'Δεν συγκρίνεται — το efood χρεώνει μία τιμή για όλο το είδος, ενώ εμείς έχουμε τιμή Delivery ανά μέγεθος. Δες τα μεγέθη από κάτω.'
                : 'Δεν συγκρίνεται — δεν έχουμε τιμή Delivery γι\' αυτό το προϊόν'}
          className={`px-1.5 py-1 rounded-lg text-sm ${same
            ? 'text-t4 hover:text-t2'
            : differs
              ? 'border border-amber-500/40 bg-amber-500/10 text-amber-800 dark:text-amber-300 font-medium hover:bg-amber-500/20'
              : 'border border-line text-t2 hover:text-t1 hover:bg-surface-3'}`}>
          {fmt(r.efood_price)}
          {same && <Check className="inline h-3 w-3 ml-0.5 opacity-60" />}
          {cmp == null && (
            <span className="ml-0.5 text-[10px] text-t4">
              {sizesAmbiguous ? 'δική μας: ανά μέγεθος' : 'χωρίς Delivery'}
            </span>
          )}
        </button>
        {decisionChip}
        {unavChip}
      </>
    );
  };

  /** Η ίδια στήλη ως κελί του πλέγματος. */
  const efoodCell = (entity: any, isSize: boolean, label: string, productId: number) => (
    <td key="ef" className="px-2 py-2 text-right whitespace-nowrap">
      {efoodInline(entity, isSize, label, productId)}
    </td>
  );

  /**
   * Ποια γραμμή efood δείχνει η ΕΝΙΑΙΑ γραμμή ενός προϊόντος ΕΝΟΣ μεγέθους (χωρίς κεφαλίδα).
   *
   * Το ίδιο προϊόν έχει δύο γραμμές efood: του είδους (`p…`) και του μεγέθους (`s…`). Όταν η
   * δεύτερη είναι ΚΑΘΡΕΦΤΗΣ (`mirrors_product`), λέει το ίδιο γεγονός και οι αποφάσεις ζουν στην
   * πρώτη — δείχνουμε του είδους. Όταν το efood τιμολογεί ΑΝΑ ΜΕΓΕΘΟΣ, η σύγκριση είναι στη
   * γραμμή του μεγέθους και του είδους λέει μόνο «ανά μέγεθος» — δείχνουμε του μεγέθους. Η
   * διπλοεγγραφή και η πρόταση ονόματος είναι γεγονότα του ΕΙΔΟΥΣ και κερδίζουν πάντα.
   */
  const singleSizeEfood = (p: any, s: any): { entity: any; isSize: boolean } => {
    const rp = efoodRowOf(p.id, false);
    const rs = efoodRowOf(s.id, true);
    if (rp && (rp.state === 'duplicate_on_platform' || rp.state === 'suggestion')) return { entity: p, isSize: false };
    if (rs && !rs.mirrors_product && rs.state !== 'absent') return { entity: s, isSize: true };
    return rp ? { entity: p, isSize: false } : { entity: s, isSize: true };
  };

  /**
   * Μία επεξεργάσιμη γραμμή ΜΕΓΕΘΟΥΣ — η μόνη τιμή που υπάρχει στον τιμοκατάλογο (Φ5). Linked =
   * single base input; unlinked = three channel inputs (empty cell shows the inherited value, muted).
   * Σε προϊόν ενός μεγέθους η γραμμή στέκεται μόνη της με το όνομα του προϊόντος (`sub` = το
   * μέγεθος) και η στήλη efood δείχνει τη γραμμή που διαλέγει το `singleSizeEfood`.
   * NOTE: this is a render FUNCTION called inline (sizeRow(...)), NOT a <Component/>. Defining
   * a component inside the parent and rendering it as <SizeRow/> gives it a new identity every
   * keystroke, so React remounts the row and the input loses focus mid-typing. Calling it as a
   * plain function reconciles the <tr> in place and keeps focus.
   */
  const sizeRow = ({ size, label, sub, indent, productId, efoodOf }: {
    size: any; label: string; sub?: string; indent?: boolean; productId: number;
    efoodOf?: { entity: any; isSize: boolean };
  }) => {
    const k = ek(size.id, true);
    const cell = draft[k] || { base: '', ta: '', di: '', dl: '' };
    const linked = !unlinked.has(k);
    // Inherited (effective) base — shown muted as a placeholder when a cell is empty.
    const inherited = effBase(size);
    const baseDisplay = inherited != null && inherited !== '' ? Number(inherited).toFixed(2) : '0.00';
    const lock = rowLock(size);
    const stored = lock ? storedListValues(size) : [];
    const ef = efoodOf || { entity: size, isSize: true };
    return (
      <tr key={k} className="border-t border-line transition-colors hover:bg-surface-2">
        <td className={`px-3 py-2 text-sm ${indent ? 'pl-10 text-t2' : 'font-medium text-t1'}`}>
          {label}
          {sub && <><span aria-hidden="true" className="mx-1.5 text-xs text-t4">·</span><span className="text-xs font-normal text-t3">{sub}</span></>}
        </td>
        {multiChannel && (
          <td className="px-2 py-2 text-center">
            {canManage && !lock && (
              <button onClick={() => toggleLink(size)} title={linked ? 'Ξεχωριστές τιμές ανά κανάλι' : 'Ενιαία τιμή για όλα τα κανάλια'}
                aria-pressed={!linked}
                className={`inline-flex h-8 w-8 items-center justify-center rounded-lg transition-colors hover:bg-surface-3 ${linked ? 'text-t4 hover:text-t1' : 'bg-brand-soft text-brand'}`}>
                {linked ? <Link2 className="h-3.5 w-3.5" /> : <Unlink className="h-3.5 w-3.5" />}
              </button>
            )}
          </td>
        )}
        {lock ? (
          // Κλειδωμένο ΠΡΙΝ την εγγραφή, με την εξήγηση ορατή.
          <td colSpan={multiChannel ? activeChan.length : 1} className="px-3 py-2">
            <div className="flex flex-wrap items-center gap-2" data-testid={`price-lock-${k}`}>
              {/* Η αποθηκευμένη τιμή ΦΑΙΝΕΤΑΙ (μόνο για ανάγνωση)· «—» μόνο όταν η γραμμή κληρονομεί. */}
              <input type="text" value={stored.join(' · ')} disabled readOnly placeholder="—"
                aria-label={t('admin:priceListLocks.lockedField')} title={lockHelp()}
                className={`${PRICE_INPUT} w-28 cursor-not-allowed border-line text-t4 opacity-60`} />
              <span className="flex items-center gap-1 text-xs text-t3">
                <Lock className="h-3 w-3 flex-shrink-0" /> {lockHelp()}
              </span>
              {stored.length > 0 && (
                <span className="text-xs text-amber-800 dark:text-amber-300" data-testid={`price-lock-stored-${k}`}>
                  {t('admin:priceListLocks.storedInList')} {stored.join(' · ')}
                </span>
              )}
              {canClearLocked(size, lock) && (
                <button type="button" onClick={() => clearLockedRow(size)}
                  title={t('admin:priceListLocks.clearHelp')}
                  className="inline-flex min-h-[30px] items-center rounded-lg border border-line-strong px-2.5 text-xs font-medium text-t2 hover:bg-surface-3">
                  {t('admin:priceListLocks.clear')}
                </button>
              )}
            </div>
          </td>
        ) : (!multiChannel || linked) ? (
          <td colSpan={multiChannel ? activeChan.length : 1} className="px-3 py-2">
            <div className="flex items-center gap-2">
              <input type="number" step="0.01" value={cell.base} disabled={!canManage}
                onChange={e => setCell(k, 'base', e.target.value)} onBlur={() => onCellBlur(size, true)}
                placeholder={baseDisplay}
                className={`${PRICE_INPUT} w-28 ${cell.base !== '' ? 'text-t1' : 'italic text-t4'}`} />
              <span className="text-xs text-t3">{multiChannel ? '€ — ενιαία (όλα τα κανάλια)' : '€'}</span>
            </div>
          </td>
        ) : (
          activeChan.map(ck => {
            const explicit = cell[ck] !== '';
            // Κενό κελί = κληρονομεί. ΤΙ κληρονομεί το λέει η υλοποίηση, όχι η
            // βάση: κατάλογος καναλιού πριν από τη βάση (βλ. effChan).
            const inhChan = effChan(size, ck);
            const chanDisplay = inhChan != null ? Number(inhChan).toFixed(2) : '0.00';
            return (
              <td key={ck} className="px-2 py-2 text-right">
                <input type="number" step="0.01" value={cell[ck]} disabled={!canManage}
                  onChange={e => setCell(k, ck, e.target.value)} onBlur={() => onCellBlur(size, false)}
                  placeholder={chanDisplay}
                  className={`${PRICE_INPUT} w-24 ${explicit ? '!border-brand font-medium text-t1' : 'italic text-t4'}`} />
              </td>
            );
          })
        )}
        {efoodOn && efoodCell(ef.entity, ef.isSize, ef.isSize ? size.name : label, productId)}
      </tr>
    );
  };

  /**
   * Προϊόν ΧΩΡΙΣ μέγεθος: δεν έχει τιμή στον τιμοκατάλογο (Φ5 — η τιμή ζει μόνο στο μέγεθος). Τέλος:
   * το ποσό έρχεται από τον πίνακα κρατικών τελών. Μη-τέλος: ανωμαλία (χρεώνεται 0 με
   * NO_SIZE_PRODUCT) — το λέμε, αντί να το κρύψουμε· διορθώνεται με μέγεθος στα «Προϊόντα».
   * Η γραμμή μένει ορατή και για τη στήλη efood του είδους.
   */
  const noSizeRow = (p: any) => {
    const fee = isFeeLockProduct(p);
    return (
      <tr key={ek(p.id, false)} className="border-t border-line transition-colors hover:bg-surface-2">
        <td className="px-3 py-2 text-sm font-medium text-t1">{p.name}</td>
        <td colSpan={(multiChannel ? 1 + activeChan.length : 1)} className="px-3 py-2">
          <span className={`flex items-center gap-1 text-xs ${fee ? 'text-t3' : 'text-amber-800 dark:text-amber-300'}`}
            data-testid={`price-nosize-${p.id}`}>
            <Lock className="h-3 w-3 flex-shrink-0" />
            {fee ? t('admin:feeNoSizesHint') : t('admin:atLeastOneSizeHint')}
          </span>
        </td>
        {efoodOn && efoodCell(p, false, p.name, p.id)}
      </tr>
    );
  };

  // Τα κουμπιά των αποφάσεων efood μέσα στα μικρά πάνελ: επιλεγμένο = απαλό χρώμα της εφαρμογής.
  const decisionBtn = (on: boolean, extra = '') => `rounded-lg border text-left transition-colors disabled:opacity-60 ${extra} ${
    on ? 'border-brand bg-brand-soft font-medium text-t1' : 'border-line text-t2 hover:bg-surface-3'
  }`;
  const panelSaveBtn = 'inline-flex items-center gap-1 rounded-lg bg-brand px-3 py-1.5 text-xs font-semibold text-brand-fg '
    + 'transition hover:brightness-110 active:scale-[0.98] disabled:opacity-50';

  const currentList = !isDefault ? lists.find(l => l.id === selected) : undefined;

  return (
    <ListPage embedded title={t('orgPages:priceLists.title')} testId="org-pricelists">
      <FlashToast toast={toast} />

      {/* Φακοί: ο Βασικός και οι τιμοκατάλογοι — ο αριθμός λέει πόσα καταστήματα ακολουθούν τον καθένα. */}
      <div className="flex flex-wrap items-center gap-2 pb-3">
        <SubTabs<string>
          label={t('orgPages:priceLists.lensLabel')}
          value={isDefault ? 'default' : String(selected)}
          onChange={id => setSelected(id === 'default' ? 'default' : Number(id))}
          tabs={[
            { id: 'default', label: 'Βασικός' },
            ...lists.map(l => ({
              id: String(l.id),
              label: (
                <>
                  {l.name}
                  {l.store_count > 0 && (
                    <span className="inline-flex items-center gap-0.5 text-xs font-medium opacity-70" title={t('orgPages:priceLists.storeCount', { count: l.store_count })}>
                      <Store className="h-3 w-3" aria-hidden="true" />{l.store_count}
                    </span>
                  )}
                </>
              ),
            })),
          ]}
          testId="pricelist-lenses"
        />
        {canManage && (
          <div className="flex items-center gap-1">
            <button type="button" onClick={() => setModal({ mode: 'create', name: '' })} className={`${GHOST_BTN} min-h-[40px]`}>
              <Plus className="h-4 w-4" aria-hidden="true" /> {t('orgPages:priceLists.new')}
            </button>
            {currentList && (
              <>
                <button type="button" onClick={() => setModal({ mode: 'rename', id: currentList.id, name: currentList.name })}
                  className={ROW_ICON_BTN} title="Μετονομασία" aria-label="Μετονομασία">
                  <Pencil className="h-4 w-4" aria-hidden="true" />
                </button>
                <button type="button" onClick={() => handleDelete(currentList)} className={ROW_DANGER_BTN} title="Διαγραφή" aria-label="Διαγραφή">
                  <Trash2 className="h-4 w-4" aria-hidden="true" />
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {/* Mode chip — consequence of editing this lens */}
      <Banner tone={isDefault ? 'info' : 'warn'} className="mb-3" testId="pricelist-mode">
        {isDefault
          ? (lists.length > 0
              ? 'Βασικός — οι αλλαγές στέλνουν αίτημα στα καταστήματα που ακολουθούν τον Βασικό. Κενό κελί καναλιού = κληρονομεί την ενιαία τιμή.'
              : 'Βασικός — οι αλλαγές εφαρμόζονται στα καταστήματα του οργανισμού. Κενό κελί καναλιού = κληρονομεί την ενιαία τιμή.')
          : 'Κάθε αλλαγή στέλνει αίτημα έγκρισης στα καταστήματα αυτού του τιμοκαταλόγου. Κενό κελί καναλιού = κληρονομεί την ενιαία τιμή.'}
      </Banner>

      {/* Bulk: set the whole list = Βασικός ± % */}
      {!isDefault && canManage && (
        <div className="mb-1 flex flex-wrap items-center gap-x-3 gap-y-2 border-t border-line py-3">
          <span className="text-sm font-medium text-t2">Μαζικά — όλος ο τιμοκατάλογος = Βασικός</span>
          <div className="flex items-center gap-1.5">
            <input type="number" step="1" value={bulkPct} onChange={e => setBulkPct(e.target.value)}
              aria-label={t('orgPages:priceLists.bulkPercent')}
              className={`${COMPACT_INPUT} w-20 text-center tabular-nums`} />
            <span className="text-sm text-t3">%</span>
          </div>
          <button type="button" onClick={handleBulk} disabled={bulkBusy} className={`${GHOST_BTN} min-h-[40px]`}>
            {bulkBusy ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : null} Εφαρμογή
          </button>
          <span className="text-xs text-t3">π.χ. 10 = +10%, -5 = −5% · αντικαθιστά τις τρέχουσες τιμές</span>
        </div>
      )}

      {/* Search + expand all + φίλτρο efood (σε κινητό τυλίγουν, δεν στριμώχνονται) */}
      <ListToolbar
        search={{ value: search, onChange: setSearch, placeholder: t('admin:searchProducts'), ariaLabel: t('admin:searchProducts') }}
        filters={(
          <>
            {(() => {
              // Ανοίγουν μόνο τα προϊόντα με ΠΟΛΛΑ μεγέθη· του ενός μεγέθους είναι ήδη μία γραμμή.
              const sizedIds = products.filter(p => (p.sizes || []).length > 1).map(p => p.id);
              const allOpen = sizedIds.length > 0 && sizedIds.every(id => expanded.has(id));
              return sizedIds.length > 0 ? (
                <button type="button" onClick={() => setExpanded(allOpen ? new Set() : new Set(sizedIds))} className={GHOST_BTN}>
                  {allOpen ? 'Σύμπτυξη όλων' : 'Ανάπτυξη όλων'}
                </button>
              ) : null;
            })()}
            {/* Το «κουμπί δίπλα»: όλη η δουλειά ελέγχου efood σε ένα φίλτρο + αριθμό. */}
            {efoodOn && (
              <button type="button" onClick={() => setOnlyEfoodDiff(v => !v)} aria-pressed={onlyEfoodDiff}
                title="Γραμμές που διαφέρουν από το efood, που δεν συμφωνούν μεταξύ καταστημάτων, που είναι καταχωρημένες δύο φορές στο efood με ΔΙΑΦΟΡΕΤΙΚΗ τιμή, ή που περιμένουν απάντηση «είναι το ίδιο;»"
                className={`${chipClass(onlyEfoodDiff)} min-h-[44px] rounded-xl`}>
                Μόνο διαφορές efood
                <span className={`rounded-full px-1.5 text-xs tabular-nums ${efoodNoteCount > 0 ? 'bg-amber-500/20 text-amber-800 dark:text-amber-200' : 'bg-surface-3 text-t3'}`}>{efoodNoteCount}</span>
              </button>
            )}
          </>
        )}
      />

      {/* Γνήσια βλάβη efood: χωρίς αυτό η απουσία στήλης διαβάζεται ως «όλα ίδια». */}
      {(efoodErr || (efoodOn && efoodMode === 'read') || (efoodOn && efoodMode === 'edit' && !canManage) || (stores.length > 0 && activeChan.length < 3)) && (
        <div className="mb-3 space-y-1.5">
          {efoodErr && (
            <p className="flex items-start gap-1.5 text-xs text-amber-800 dark:text-amber-300">
              <AlertCircle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" /> {efoodErr}
            </p>
          )}
          {efoodOn && efoodMode === 'read' && (
            <p className="flex items-center gap-1.5 text-xs text-t3">
              <Info className="h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" /> Η στήλη efood είναι σε κατάσταση «ανάγνωσης»: βλέπεις τις τιμές, δεν αποθηκεύονται αποφάσεις.
            </p>
          )}
          {efoodOn && efoodMode === 'edit' && !canManage && (
            <p className="flex items-center gap-1.5 text-xs text-t3">
              <Info className="h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" /> Οι αποφάσεις τιμής efood χρειάζονται δικαιώματα διαχειριστή του οργανισμού.
            </p>
          )}
          {stores.length > 0 && activeChan.length < 3 && (
            <p className="flex items-center gap-1.5 text-xs text-t3">
              <Info className="h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" /> Εμφανίζονται μόνο τα κανάλια που χρησιμοποιούν τα καταστήματα: {activeChan.map(c => CHAN_LABEL[c]).join(' · ')}.
            </p>
          )}
        </div>
      )}

      {/* Matrix — πίνακας στο ύφος του «Πίνακα»: επικεφαλίδες t3, λεπτή γραμμή ανάμεσα, χωρίς κουτί. */}
      <div className="-mx-3 overflow-x-auto" data-testid="pricelist-matrix">
        <table className="w-full min-w-[640px] text-sm">
          <thead>
            <tr className="border-b border-line-strong text-left">
              <th className={TH}>Προϊόν / Μέγεθος</th>
              {multiChannel && (
                <th className={`${TH} w-12 text-center`}>
                  <Link2 className="mx-auto h-3.5 w-3.5 text-t4" aria-hidden="true" />
                  <span className="sr-only">{t('orgPages:priceLists.linkColumn')}</span>
                </th>
              )}
              {multiChannel
                ? activeChan.map(ck => <th key={ck} className={`${TH} text-right`}>{CHAN_LABEL[ck]}</th>)
                : <th className={`${TH} text-right`}>Τιμή</th>}
              {/* Αμέσως μετά το Delivery: η τιμή που βλέπει ο πελάτης στο efood. */}
              {efoodOn && (
                <th className={`${TH} whitespace-nowrap text-right`}>
                  efood {efoodLoading && <Loader2 className="ml-1 inline h-3 w-3 animate-spin" />}
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {filtered.map(p => {
              const sizes = p.sizes || [];
              if (sizes.length === 0) return noSizeRow(p);
              // ΕΝΑ μέγεθος: μία γραμμή, χωρίς κεφαλίδα και άνοιγμα — το μέγεθος ΕΙΝΑΙ το
              // προϊόν (147 από τα 246 των Κουλουράδων). Εκεί ζει και η τιμή Delivery που
              // συγκρίνεται με το efood, άρα το φίλτρο «μόνο διαφορές» τη δείχνει πάντα.
              if (sizes.length === 1) {
                return sizeRow({
                  size: sizes[0], label: p.name, sub: sizes[0].name, productId: p.id,
                  efoodOf: singleSizeEfood(p, sizes[0]),
                });
              }
              // Με το φίλτρο «μόνο διαφορές efood» τα μεγέθη ΑΝΟΙΓΟΥΝ μόνα τους:
              // αλλιώς το φίλτρο θα έδειχνε 12 κλειστά προϊόντα και καμία διαφορά.
              const open = expanded.has(p.id) || (onlyEfoodDiff && efoodOn);
              const shownSizes = (onlyEfoodDiff && efoodOn)
                ? sizes.filter((s: any) => efoodNoteworthy(s.id, true))
                : sizes;
              const feeLocked = !isDefault && isFeeLockProduct(p);
              return (
                <React.Fragment key={p.id}>
                  <tr className={`border-t border-line transition-colors hover:bg-surface-2 ${open ? 'bg-surface-2/60' : ''}`}>
                    <td className="px-3 py-2.5" colSpan={totalCols}>
                      <div className="flex items-center justify-between gap-2">
                        <button onClick={() => setExpanded(s => { const n = new Set(s); n.has(p.id) ? n.delete(p.id) : n.add(p.id); return n; })}
                          aria-expanded={open}
                          className="flex min-h-[32px] items-center gap-2 rounded-lg text-left">
                          {open ? <ChevronDown className="h-4 w-4 text-t4" aria-hidden="true" /> : <ChevronRight className="h-4 w-4 text-t4" aria-hidden="true" />}
                          <span className="font-medium text-t1">{p.name}</span>
                          <span className="text-xs text-t3">({sizes.length} μεγέθη)</span>
                          {/* Μεγέθη τέλους (κατάλοιπο) σε τιμοκατάλογο: κλειδωμένα — το ποσό έρχεται από το state_fees. */}
                          {feeLocked && (
                            <span className="flex items-center gap-1 text-xs text-t3" title={lockHelp()}
                              data-testid={`price-lock-${ek(p.id, false)}`}>
                              <Lock className="h-3 w-3" aria-label={t('admin:priceListLocks.lockedField')} />
                              {t('admin:priceListLocks.feeShort')}
                            </span>
                          )}
                        </button>
                        {/*
                          Το efood του ΙΔΙΟΥ του είδους (όχι των μεγεθών). ΓΙΑΤΙ: η
                          γραμμή-κεφαλίδα καταλαμβάνει όλες τις στήλες, άρα χωρίς
                          αυτό μια πρόταση ή διαφορά σε επίπεδο είδους δεν θα
                          φαινόταν ΠΟΤΕ. Έξω από το <button> — τα κουμπιά δεν
                          φωλιάζουν.
                        */}
                        {efoodOn && (() => {
                          const r = efood[ek(p.id, false)];
                          // Και η ΗΣΥΧΗ διπλοεγγραφή φαίνεται εδώ: δεν ζητά
                          // απόφαση, αλλά «το προϊόν υπάρχει 2 φορές στο efood»
                          // είναι γεγονός του ΕΙΔΟΥΣ — αν δεν μπει στη
                          // γραμμή-κεφαλίδα, σε κλειστό προϊόν δεν το βλέπει κανείς.
                          //
                          // ΚΑΙ ΟΤΑΝ ΤΟ EFOOD ΧΡΕΩΝΕΙ ΣΤΟ ΕΙΔΟΣ, ΠΑΝΤΑ: τότε ο
                          // ΜΟΝΟΣ αριθμός efood του προϊόντος είναι αυτός εδώ —
                          // τα μεγέθη από κάτω δεν έχουν δικό τους, και η
                          // κεφαλίδα εξηγεί «εμείς τιμολογούμε ανά μέγεθος».
                          const says = !!r && (isPlatformRowNoteworthy(r) || r.state === 'absent'
                            || r.state === 'duplicate_on_platform' || (Number(r.unavailable_stores) || 0) > 0
                            || (r.efood_price != null && rowPricesItemLevel(r)));
                          return says
                            ? <span className="flex-shrink-0 whitespace-nowrap">{efoodInline(p, false, p.name, p.id)}</span>
                            : null;
                        })()}
                      </div>
                    </td>
                  </tr>
                  {open && shownSizes.map((s: any) => sizeRow({ size: s, label: s.name, indent: true, productId: p.id }))}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
        {filtered.length === 0 && <EmptyState title={t('admin:noProductsFound')} />}
      </div>

      {/* Stores grouped by price list */}
      <div className="mt-6">
        <Disclosure label="Καταστήματα ανά τιμοκατάλογο" open={showStores} onToggle={setShowStores} testId="pricelist-stores">
          <div className="divide-y divide-line">
            {[{ id: null as number | null, name: 'Βασικός' }, ...lists.map(l => ({ id: l.id as number | null, name: l.name }))].map(grp => {
              const grpStores = stores.filter(s => (s.price_list_id ?? null) === grp.id);
              return (
                <div key={grp.id ?? 'default'} className="py-3">
                  <div className="mb-2 text-sm font-semibold text-t2">
                    {grp.name} <span className="font-normal text-t4">({grpStores.length})</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {grpStores.map(s => (
                      <div key={s.id} className="flex items-center gap-1.5 rounded-lg border border-line bg-surface-2 py-1 pl-2.5 pr-1">
                        <span className="text-sm text-t1">{s.name} <span className="text-t4">#{s.id}</span></span>
                        {canManage && (
                          <select value={grp.id ?? 'default'} onChange={e => assignStore(s.id, e.target.value === 'default' ? 'default' : Number(e.target.value))}
                            aria-label={t('orgPages:priceLists.assignStore', { name: s.name })}
                            className="h-8 rounded-md border border-line-strong bg-surface-1 px-1.5 text-xs text-t2 focus:outline-none focus:ring-2 focus:ring-ring/50">
                            <option value="default">Βασικός</option>
                            {lists.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                          </select>
                        )}
                      </div>
                    ))}
                    {grpStores.length === 0 && <span className="text-sm italic text-t4">— κανένα κατάστημα</span>}
                  </div>
                </div>
              );
            })}
            {stores.length === 0 && <div className="py-4 text-center text-sm text-t4">Δεν υπάρχουν καταστήματα</div>}
          </div>
        </Disclosure>
      </div>

      {/*
        Πάνελ απόφασης τιμής efood. ΜΙΚΡΟ και δίπλα στο κελί, όχι modal ολόκληρης
        οθόνης: η Κασσιανή ελέγχει μια τιμή, δεν μπαίνει σε άλλη εργασία. Θέση
        `fixed` επειδή το πλέγμα έχει overflow-x και θα έκοβε ό,τι είναι absolute.
      */}
      {panel && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => { if (!panelBusy) setPanel(null); }} />
          {/* max-height: η διπλοεγγραφή προσθέτει μία κάρτα ΑΝΑ καταχώρηση — χωρίς
              αυτό το πάνελ θα έβγαινε κάτω από το παράθυρο και τα κουμπιά
              απόφασης δεν θα ήταν προσβάσιμα. */}
          <div className={`${POPOVER} space-y-2 overflow-y-auto`} data-testid="efood-panel"
            style={{ top: panel.at.top, left: panel.at.left, width: PANEL_W, maxHeight: `calc(100vh - ${panel.at.top + 12}px)` }}>
            <div className="flex items-start justify-between gap-2">
              <span className="truncate text-sm font-semibold text-t1">{panel.label}</span>
              <button onClick={() => setPanel(null)} className="-m-1 rounded-md p-1 text-t3 hover:bg-surface-3 hover:text-t1" aria-label={t('orgPages:common.close')}><X className="h-4 w-4" /></button>
            </div>

            <div className="flex items-center justify-between text-xs text-t2">
              <span>Delivery — η τιμή μας</span>
              <span className="font-medium tabular-nums text-t1">{panel.row.delivery_price != null ? fmt(panel.row.delivery_price) : '—'}</span>
            </div>
            <div className="flex items-center justify-between text-xs text-t2">
              <span>efood — τι πληρώνει ο πελάτης</span>
              <span className="font-medium tabular-nums text-t1">{efoodPriceText(panel.row)}</span>
            </div>

            {/*
              ΔΙΠΛΟΕΓΓΡΑΦΗ — ΜΙΑ ΓΡΑΜΜΗ ΑΝΑ ΚΑΤΑΧΩΡΗΣΗ efood.
              Η πρώτη πρόταση εξηγεί ΓΙΑΤΙ υπάρχουν δύο: χωρίς αυτήν η Κασσιανή
              το διαβάζει ως σφάλμα δικό μας και ψάχνει τι «χάλασε». Δεν είναι:
              στους Κουλουράδες η κατηγορία «Νηστίσιμα» ξαναγράφει είδη που ζουν
              και στα «Γλυκά»/«Αλμυρά» — 10 ονόματα, και στα 20 καταστήματα.
              Η απόφαση μπαίνει ΑΝΑ ΚΑΤΑΧΩΡΗΣΗ (δικό της master_key): «το
              νηστίσιμο κράτα το, το γλυκό πάρε τη δική μας» είναι δύο αποφάσεις.
            */}
            {panelIsDup && (
              <div className="space-y-1.5">
                {/*
                  ΔΥΟ ΑΡΙΘΜΟΙ, ΔΥΟ ΠΡΟΤΑΣΕΙΣ. Το `duplicate_max_per_store` λέει
                  πόσες φορές το βλέπει ο ΠΕΛΑΤΗΣ σε ΕΝΑ κατάστημα· οι κάρτες
                  από κάτω είναι οι καταχωρήσεις ΟΛΗΣ της ομάδας — και δεν είναι
                  ο ίδιος αριθμός. Μετρημένο: «κουλουρι πραλινα νηστισιμη» των
                  Κουλουράδων έχει ΤΡΙΑ master δια καταστημάτων αλλά το πολύ ΔΥΟ
                  ανά κατάστημα. Το παλιό κείμενο έγραφε «2 φορές … σε δύο
                  κατηγορίες» και ακριβώς από κάτω έδειχνε ΤΡΕΙΣ κάρτες.
                */}
                <p className="rounded-lg border border-line bg-surface-2 px-2 py-1 text-[11px] text-t2">
                  Το ίδιο είδος είναι καταχωρημένο σε <b>περισσότερες από μία κατηγορίες</b> του efood
                  (π.χ. «Νηστίσιμα» δίπλα στα «Γλυκά»). Δεν είναι λάθος — έτσι το πουλάει το efood.
                  Η απόφαση μπαίνει <b>σε καθεμιά ξεχωριστά</b>.
                  {rowDuplicateCount(panel.row) > 1 && (
                    <> Ο πελάτης το βλέπει έως <b>{rowDuplicateCount(panel.row)} φορές</b> σε ένα κατάστημα
                      {Number(panel.row.duplicate_stores) > 0
                        ? <>, και αυτό συμβαίνει σε {Number(panel.row.duplicate_stores)}{' '}
                          {Number(panel.row.duplicate_stores) === 1 ? 'κατάστημα' : 'καταστήματα'} της ομάδας.</>
                        : '.'}
                    </>
                  )}
                  {panelDupEntries.length > 0 && (
                    <> Σε όλη την ομάδα βρέθηκαν <b>{panelDupEntries.length}</b>{' '}
                      {panelDupEntries.length === 1 ? 'καταχώρηση' : 'καταχωρήσεις'} — μία κάρτα η καθεμιά.</>
                  )}
                </p>
                {panel.row.also_differs_between_stores === true && (
                  <p className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-2 py-1 text-[11px] text-amber-800 dark:text-amber-300">
                    Επιπλέον, τα καταστήματα της ομάδας δεν έχουν την ίδια τιμή — δες τη λίστα ανά κατάστημα πιο κάτω.
                  </p>
                )}
                {panelDupEntries.length === 0 && (
                  <p className="text-[11px] text-t4">
                    Η ανάλυση ανά καταχώρηση δεν ήρθε από τον server — η απόφαση γίνεται από την οθόνη «Πλατφόρμες».
                  </p>
                )}
                {panelDupEntries.map((entry, ei) => {
                  const mode: OverrideMode = (entry.override && entry.override.mode) || 'catalog';
                  const busy = dupBusyKey === entry.master_key;
                  const unav = Number(entry.unavailable_stores) || 0;
                  // ΕΝΑ ΟΝΟΜΑ ΓΙΑ ΤΗΝ ΚΑΡΤΗ — και στο tooltip, και στα aria-label
                  // των κουμπιών, και στο πεδίο ποσού. Το `master_key` μένει
                  // ΜΟΝΟ κλειδί React και ωφέλιμο φορτίο του PUT.
                  const entryLabel = panelDupLabels[ei];
                  return (
                    <div key={entry.master_key} className="space-y-1 rounded-lg border border-line p-2">
                      <div className="flex items-baseline justify-between gap-2">
                        <span className="truncate text-[11px] text-t1" title={(entry.name || '').trim() || entryLabel}>
                          {entryLabel}
                        </span>
                        <span className="flex-shrink-0 text-[11px] font-medium tabular-nums text-t1">
                          {dupEntryPriceText(entry)}
                        </span>
                      </div>
                      <div className="flex flex-wrap items-center gap-1 text-[10px] text-t4">
                        <span>{entry.stores} {entry.stores === 1 ? 'κατάστημα' : 'καταστήματα'}</span>
                        {unav > 0 && (
                          <span className="flex items-center gap-0.5 text-red-700 dark:text-red-300">
                            <EyeOff className="h-3 w-3" /> μη διαθέσιμο σε {unav}
                          </span>
                        )}
                        <span>· {DECISION_LABEL[mode]}</span>
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {DECISION_OPTS.map(([m, long, short]) => (
                          <button key={m} disabled={!efoodCanAct || busy || panelBusy}
                            aria-label={`${long} — ${entryLabel}`}
                            title={long}
                            onClick={() => onEntryDecision(entry, m)}
                            className={decisionBtn(mode === m, 'px-1.5 py-1 text-[10px]')}>
                            {short}
                          </button>
                        ))}
                        {busy && <Loader2 className="h-3 w-3 animate-spin text-t3" />}
                      </div>
                      {dupFixedFor === entry.master_key && (
                        <div className="flex items-center gap-1">
                          <input type="number" step="0.01" value={dupFixed} disabled={!efoodCanAct}
                            onChange={e => setDupFixed(e.target.value)} placeholder="π.χ. 2.50"
                            aria-label={`σταθερή τιμή — ποσό για ${entryLabel}`}
                            className={`${PRICE_INPUT} h-8 flex-1 text-xs`} />
                          <button disabled={!efoodCanAct || busy || panelBusy}
                            onClick={() => saveEfoodEntryDecision(entry, 'fixed', dupFixed)}
                            className={panelSaveBtn}>
                            Αποθήκευση
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/*
              Ανά κατάστημα: η απάντηση στο «γιατί διαφορετικές;». Το `per_store`
              είναι `null` ΡΗΤΑ όταν όλα τα καταστήματα συμφωνούν και είναι
              ανοιχτά — τότε γράφουμε μία πρόταση αντί για 18 ίδιες γραμμές.
            */}
            {panel.row.per_store === null && (
              <p className="text-[11px] text-t4">
                Ίδια τιμή και στα {panel.row.stores_with_item} καταστήματα της ομάδας που το έχουν.
              </p>
            )}
            {panel.row.per_store && panel.row.per_store.length > 0 && (
              <div className="max-h-28 divide-y divide-line overflow-y-auto rounded-lg border border-line">
                {/*
                  ΚΛΕΙΔΙ = ΚΑΤΑΣΤΗΜΑ + ΚΕΝΤΡΙΚΟ ΕΙΔΟΣ, όχι σκέτο κατάστημα: στη
                  διπλοεγγραφή ΤΟ ΙΔΙΟ κατάστημα δίνει δύο γραμμές (δύο master).
                  Με σκέτο `store_id` η React θα κρατούσε ΜΙΑ από τις δύο και η
                  λίστα θα έκρυβε ακριβώς την πληροφορία για την οποία ανοίγει.
                  Και το όνομα της κατηγορίας μπαίνει δίπλα, αλλιώς οι δύο
                  γραμμές του ίδιου καταστήματος είναι δυσεξήγητες.
                */}
                {panel.row.per_store.map((s, i) => {
                  const cat = panelIsDup ? panelDupCategory(s.master_key) : '';
                  return (
                    <div key={`${s.store_id}#${s.master_key || i}`} className="flex items-center justify-between gap-2 px-2 py-1 text-[11px]">
                      <span className="truncate text-t2" title={s.name || undefined}>
                        {storeLabel(s.store_id)}{cat ? <span className="text-t4"> · {cat}</span> : null}
                      </span>
                      <span className="flex flex-shrink-0 items-center gap-1">
                        {s.is_available === false && <EyeOff className="h-3 w-3 text-red-600 dark:text-red-400" />}
                        <span className="tabular-nums text-t1">{s.price != null ? fmt(s.price) : '—'}</span>
                      </span>
                    </div>
                  );
                })}
              </div>
            )}

            {panel.row.missing_store_ids && panel.row.missing_store_ids.length > 0 && (
              <p className="text-[11px] text-t4">
                Δεν υπάρχει στο efood σε {panel.row.missing_store_ids.length}{' '}
                {panel.row.missing_store_ids.length === 1 ? 'κατάστημα' : 'καταστήματα'} της ομάδας.
              </p>
            )}

            {/*
              ΤΙ ΠΙΑΝΕΙ Η ΑΠΟΦΑΣΗ — γραμμένο ΠΑΝΩ από τα κουμπιά, όχι μετά.
              (α) Όλα τα μεγέθη: το efood έχει ΕΝΑ κεντρικό είδος ανά προϊόν, τα
                  μεγέθη είναι επιλογές του· δεν υπάρχει κλειδί ανά μέγεθος.
              (β) Όλη η αλυσίδα: το override δεν έχει στήλη τιμοκαταλόγου.
            */}
            {panel.row.org_size_id != null && siblingSizeNames(panel.row).length > 1 && (
              <p className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-2 py-1 text-[11px] text-amber-800 dark:text-amber-300">
                Η απόφαση ισχύει για <b>όλα τα μεγέθη</b> του «{productNameOf(panel.row)}»
                {' '}({siblingSizeNames(panel.row).join(', ')}) — το efood κρατά ένα είδος ανά προϊόν, όχι ανά μέγεθος.
              </p>
            )}
            <p className={`rounded-lg px-2 py-1 text-[11px] ${decisionWiderThanGroup
              ? 'border border-amber-500/30 bg-amber-500/10 text-amber-800 dark:text-amber-300'
              : 'text-t4'}`}>
              {decisionWiderThanGroup
                ? <>Ισχύει για <b>όλα τα {stores.length} καταστήματα της αλυσίδας</b>, όχι μόνο για τα {groupStoreCount} αυτού του τιμοκαταλόγου.</>
                : 'Ισχύει για όλα τα καταστήματα της αλυσίδας.'}
            </p>

            {/* ΜΙΑ απόφαση για ΜΙΑ γραμμή. Στη διπλοεγγραφή δεν υπάρχει «μία»:
                τα κουμπιά είναι πιο πάνω, ανά καταχώρηση efood. Ένα κοινό
                «Αποθήκευση» εδώ θα έγραφε την ίδια απόφαση και στα δύο είδη —
                ακριβώς αυτό που δεν θέλει ο άνθρωπος που τα ξεχωρίζει. */}
            {!panelIsDup && (
              <div className="space-y-1 pt-1">
                <div className="text-[10px] font-semibold uppercase tracking-wide text-t4">Τι ισχύει για το efood</div>
                {DECISION_OPTS.map(opt => (
                  <button key={opt[0]} disabled={!efoodCanAct} onClick={() => setPanelMode(opt[0])}
                    className={decisionBtn(panelMode === opt[0], 'w-full px-2 py-1.5 text-xs')}>
                    {opt[1]}
                  </button>
                ))}
                {panelMode === 'fixed' && (
                  <input type="number" step="0.01" value={panelFixed} disabled={!efoodCanAct}
                    onChange={e => setPanelFixed(e.target.value)} placeholder="π.χ. 2.50"
                    className={`${PRICE_INPUT} h-8 w-full text-xs`} />
                )}
              </div>
            )}

            {panelErr && <p className="text-[11px] text-amber-800 dark:text-amber-300">{panelErr}</p>}
            {!efoodCanAct && (
              <p className="text-[11px] text-t4">
                {efoodMode === 'read'
                  ? 'Κατάσταση «ανάγνωσης»: η απόφαση δεν αποθηκεύεται.'
                  : 'Οι αποφάσεις τιμής χρειάζονται δικαιώματα διαχειριστή του οργανισμού.'}
              </p>
            )}

            {efoodCanAct && !panelIsDup && (
              <div className="flex justify-end gap-2 pt-1">
                <button onClick={() => setPanel(null)} className="rounded-lg px-2.5 py-1.5 text-xs font-medium text-t2 hover:bg-surface-3 hover:text-t1">Άκυρο</button>
                <button onClick={saveEfoodDecision} disabled={panelBusy} className={panelSaveBtn}>
                  {panelBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : null} Αποθήκευση
                </button>
              </div>
            )}
            {/* Διπλοεγγραφή: κάθε κουμπί καταχώρησης γράφει μόνο του — δεν
                υπάρχει «Αποθήκευση» που να μαζεύει δύο αποφάσεις σε μία. */}
            {panelIsDup && (
              <div className="flex justify-end pt-1">
                <button onClick={() => setPanel(null)} disabled={panelBusy}
                  className="rounded-lg px-2.5 py-1.5 text-xs font-medium text-t2 hover:bg-surface-3 hover:text-t1 disabled:opacity-50">Κλείσιμο</button>
              </div>
            )}
          </div>
        </>
      )}

      {/*
        «Αυτό είναι το ίδιο προϊόν;» — η αντιστοίχιση ονόματος μέσα στον
        τιμοκατάλογο. Μία ερώτηση, δύο απαντήσεις· ό,τι δεν μοιάζει με κανένα
        δικό μας προϊόν ΔΕΝ έχει γραμμή εδώ και μένει στην οθόνη «Αντιστοιχίσεις».
      */}
      {ask && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => { if (!askBusy) setAsk(null); }} />
          <div className={`${POPOVER} space-y-2`} data-testid="efood-ask"
            style={{ top: ask.at.top, left: ask.at.left, width: PANEL_W }}>
            <div className="flex items-start justify-between gap-2">
              <span className="text-sm font-semibold text-t1">Αυτό είναι το ίδιο προϊόν;</span>
              <button onClick={() => setAsk(null)} className="-m-1 rounded-md p-1 text-t3 hover:bg-surface-3 hover:text-t1" aria-label={t('orgPages:common.close')}><X className="h-4 w-4" /></button>
            </div>
            {(() => {
              const s = ask.row.suggestion;
              const range = s && s.min_price != null
                ? (s.max_price != null && s.max_price !== s.min_price
                    ? `${fmt(s.min_price)} – ${fmt(s.max_price)}`
                    : fmt(s.min_price))
                : '';
              return (
                <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-2">
                  <div className="text-[10px] font-semibold uppercase tracking-wide text-t4">στο efood</div>
                  <div className="break-words text-sm text-t1">{s ? s.display_name : '—'}</div>
                  <div className="text-[11px] text-t3">
                    {s && s.stores ? `${s.stores} ${s.stores === 1 ? 'κατάστημα' : 'καταστήματα'}` : ''}
                    {range ? ` · ${range}` : ''}
                  </div>
                  {/* ΦΠΑ: η φάση 1 το σημαδεύει — μια λάθος αντιστοίχιση εδώ
                      χρεώνει λάθος συντελεστή σε κάθε απόδειξη. */}
                  {s && s.vat_risk === true && (
                    <div className="mt-1 flex items-center gap-1 text-[11px] text-red-700 dark:text-red-300">
                      <AlertCircle className="h-3 w-3 shrink-0" aria-hidden="true" /> διαφορετικός ΦΠΑ από τον προεπιλεγμένο της πλατφόρμας — έλεγξέ το
                    </div>
                  )}
                </div>
              );
            })()}
            <div className="rounded-lg border border-line p-2">
              <div className="text-[10px] font-semibold uppercase tracking-wide text-t4">δικό μας</div>
              <div className="break-words text-sm text-t1">{ask.label}</div>
            </div>
            {askErr && <p className="text-[11px] text-amber-800 dark:text-amber-300">{askErr}</p>}
            {!efoodCanMapNow && (
              <p className="text-[11px] text-t4">
                {efoodMode === 'read'
                  ? 'Κατάσταση «ανάγνωσης»: η αντιστοίχιση δεν αποθηκεύεται.'
                  : 'Η αντιστοίχιση χρειάζεται δικαιώματα διαχείρισης του οργανισμού.'}
              </p>
            )}
            {ask.row.suggestion && ask.row.suggestion.mapping_id == null && (
              <p className="text-[11px] text-t4">
                Δεν υπάρχει γραμμή αντιστοίχισης για αυτό το όνομα — γίνεται από την οθόνη «Αντιστοιχίσεις».
              </p>
            )}
            <div className="flex justify-end gap-2">
              <button onClick={() => setAsk(null)} className="rounded-lg px-2.5 py-1.5 text-xs font-medium text-t2 hover:bg-surface-3 hover:text-t1">Όχι</button>
              <button onClick={confirmEfoodMapping}
                disabled={askBusy || !efoodCanMapNow || !ask.row.suggestion || ask.row.suggestion.mapping_id == null}
                className={panelSaveBtn}>
                {askBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />} Ναι, το ίδιο
              </button>
            </div>
            <p className="text-[10px] text-t4">Το «Όχι» απλώς κλείνει — το όνομα μένει για την οθόνη «Αντιστοιχίσεις».</p>
          </div>
        </>
      )}

      {/* Create / rename price list — συρτάρι της νέας εμφάνισης */}
      <DetailPanel
        open={!!modal}
        onClose={() => { if (!modalBusy) setModal(null); }}
        title={modal?.mode === 'rename' ? 'Μετονομασία' : 'Νέος τιμοκατάλογος'}
        subtitle={modal?.mode === 'rename' && currentList ? currentList.name : undefined}
        footer={(
          <>
            <button type="button" onClick={() => setModal(null)} className={GHOST_BTN}>Άκυρο</button>
            <button type="submit" form={modalFormId} disabled={modalBusy || !modal || !modal.name.trim()} className={PRIMARY_BTN}>
              {modalBusy ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : null} {modal?.mode === 'create' ? 'Δημιουργία' : 'Αποθήκευση'}
            </button>
          </>
        )}
        testId="pricelist-modal"
      >
        {modal && (
          <form id={modalFormId} className="space-y-5" onSubmit={e => { e.preventDefault(); submitModal(); }}>
            <SheetField label={t('orgPages:priceLists.name')} htmlFor={`${modalFormId}-name`}>
              <input id={`${modalFormId}-name`} autoFocus type="text" value={modal.name}
                onChange={e => setModal(m => m && { ...m, name: e.target.value })}
                placeholder="π.χ. Premium" className={INPUT_CLS} />
            </SheetField>
            {modal.mode === 'create' && (
              <SheetField
                label="Αντιγραφή τιμών από"
                htmlFor={`${modalFormId}-copy`}
                hint={modal.copyFromId
                  ? `Αντιγράφει όλες τις τιμές του «${lists.find(l => l.id === modal.copyFromId)?.name}» — μετά άλλαξε μόνο όσες διαφέρουν.`
                  : 'Ξεκινά ίδιος με τον Βασικό (κληρονομεί όλες τις τιμές) — άλλαξε μόνο όσες διαφέρουν, ή χρήσε το «μαζικά ±%».'}
              >
                <select id={`${modalFormId}-copy`}
                  value={modal.copyFromId ?? ''}
                  onChange={e => setModal(m => m && { ...m, copyFromId: e.target.value ? parseInt(e.target.value) : null })}
                  className={SELECT_CLS}>
                  <option value="">Βασικός κατάλογος (κληρονομεί)</option>
                  {lists.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                </select>
              </SheetField>
            )}
          </form>
        )}
      </DetailPanel>
    </ListPage>
  );
};

export default OrgPriceListsManager;
